import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import { liveSessionAPI, practiceVideoAPI } from "../../services/api";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import {
  FiVideo, FiCheckCircle, FiClock, FiEyeOff, FiUpload,
  FiPlus, FiSearch, FiEdit, FiEye, FiArchive, FiSend,
  FiX, FiCheck, FiStar, FiActivity, 
  FiList, FiRefreshCw, 
  FiTrendingUp, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiFilter, FiLayers, FiUser, FiGlobe, FiLock, FiTag,
  FiAlertTriangle, FiAlertCircle
} from "react-icons/fi";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive: "#6B8A5A", olive2: "#8BA47A",
  orange: "#F29A0C", orange2: "#E97818",
  dark: "#1F1F1F", mid: "#555", muted: "#AAA", light: "#CCC",
  border: "rgba(239,229,216,0.8)",
  green: "#22C55E", greenDk: "#16A34A",
  red: "#E24B4A", amber: "#F59E0B",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)", WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── CUSTOM DROPDOWN COMPONENT (With Search & Dynamic Height) ───────────────────
function CustomDropdown({ value, onChange, options, label, icon: Icon, fullWidth = false, align = "left", searchable = true, placeholder = "Select..." }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const selectedOption = options.find((o) => String(o.value) === String(value));

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter((o) =>
      String(o.label).toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  }, [options, searchTerm]);

  return (
    <div 
      className={`relative ${fullWidth ? "w-full" : "inline-block"} text-left`} 
      style={{ zIndex: open ? 999 : 20 }}
      ref={dropdownRef}
    >
      <motion.button
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-2 ${fullWidth ? "w-full rounded-[13px] px-3 py-2 text-[12px]" : "rounded-full px-4 py-2 text-[12px]"} font-semibold border cursor-pointer outline-none transition-all shadow-sm`}
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.85)",
          borderColor: open ? T.olive : "rgba(239,229,216,0.8)",
          color: selectedOption ? "#1F1F1F" : T.muted,
          boxShadow: open ? "0 4px 20px rgba(107,138,90,0.18)" : "none",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-1.5 truncate min-w-0">
          {Icon && <Icon size={13} style={{ color: open ? T.olive : T.muted }} className="flex-shrink-0" />}
          <span className="truncate">{selectedOption ? selectedOption.label : (label || placeholder || "Select...")}</span>
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center ml-0.5 flex-shrink-0"
        >
          <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted }} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={`ad-dd-menu absolute ${align === "right" ? "right-0" : "left-0"} mt-1.5 ${fullWidth ? "w-full min-w-[180px]" : "min-w-[220px] max-w-[320px]"} rounded-[20px] p-2 shadow-2xl text-left flex flex-col`}
            style={{
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(239,229,216,0.9)",
              boxShadow: "0 16px 40px rgba(0,0,0,0.16)",
              maxHeight: "340px",
              zIndex: 1000,
            }}
          >
            {/* Search Bar inside Dropdown Popover */}
            {searchable && options.length > 3 && (
              <div className="p-1 mb-1.5 border-b border-zinc-100/80 sticky top-0 bg-white/90 backdrop-blur-md z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-[#6B8A5A] transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {label && (
              <div className="px-2 py-1 mb-1 text-[9px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                {label}
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-[260px] pr-0.5" style={{ scrollbarWidth: "thin", msOverflowStyle: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11.5px] text-zinc-400 italic">
                  No matching items found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <motion.button
                      key={String(opt.value)}
                      type="button"
                      whileHover={{ x: 2, background: "rgba(107,138,90,0.08)" }}
                      onClick={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 text-[12px] font-semibold rounded-[10px] border-none bg-transparent cursor-pointer transition-all mb-0.5 text-left"
                      style={{
                        color: isSelected ? T.olive : "#333",
                        fontFamily: "'Inter', sans-serif",
                        background: isSelected ? "rgba(107,138,90,0.1)" : "transparent",
                      }}
                    >
                      <span className="flex items-center gap-2 truncate min-w-0">
                        {opt.dotColor && (
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: opt.dotColor }}
                          />
                        )}
                        <span className="truncate">{opt.label}</span>
                      </span>
                      {isSelected && <FiCheck size={13} style={{ color: T.olive }} className="flex-shrink-0 ml-1" />}
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TIER_STYLES = {
  Free:            { bg:"rgba(200,200,200,0.12)", c:"#666",    b:"rgba(200,200,200,0.3)"  },
  "Free Trial":    { bg:"rgba(200,200,200,0.12)", c:"#666",    b:"rgba(200,200,200,0.3)"  },
  Basic:           { bg:"rgba(24,95,165,0.10)",   c:"#0C447C", b:"rgba(24,95,165,0.2)"   },
  "Basic Plan":    { bg:"rgba(24,95,165,0.10)",   c:"#0C447C", b:"rgba(24,95,165,0.2)"   },
  Standard:        { bg:"rgba(83,74,183,0.10)",   c:"#3C3489", b:"rgba(83,74,183,0.2)"   },
  "Standard Plan": { bg:"rgba(83,74,183,0.10)",   c:"#3C3489", b:"rgba(83,74,183,0.2)"   },
  Premium:         { bg:"rgba(242,154,12,0.12)",  c:"#C8660A", b:"rgba(242,154,12,0.25)" },
  "Premium Plan":  { bg:"rgba(242,154,12,0.12)",  c:"#C8660A", b:"rgba(242,154,12,0.25)" },
  Pro:             { bg:"rgba(107,138,90,0.12)",  c:"#27500A", b:"rgba(107,138,90,0.25)" },
  "YTT Pro":       { bg:"rgba(107,138,90,0.12)",  c:"#27500A", b:"rgba(107,138,90,0.25)" },
};

const matchTier = (vTier, filterVal) => {
  if (!filterVal || filterVal === "all") return true;
  if (!vTier) return false;
  const vLow = String(vTier).toLowerCase().trim();
  const fLow = String(filterVal).toLowerCase().trim();

  if (vLow === fLow) return true;

  // Free vs Free Trial matching
  const isVFree = vLow === "free" || vLow.includes("free") || vLow.includes("trial");
  const isFFree = fLow === "free" || fLow.includes("free") || fLow.includes("trial");
  if (isVFree && isFFree) return true;

  // Basic Plan matching
  const isVBasic = vLow.includes("basic");
  const isFBasic = fLow.includes("basic");
  if (isVBasic && isFBasic) return true;

  // Standard Plan matching
  const isVStandard = vLow.includes("stand");
  const isFStandard = fLow.includes("stand");
  if (isVStandard && isFStandard) return true;

  // Premium Plan matching
  const isVPremium = vLow.includes("prem");
  const isFPremium = fLow.includes("prem");
  if (isVPremium && isFPremium) return true;

  // Pro / YTT Pro matching
  const isVPro = vLow.includes("pro") || vLow.includes("ytt");
  const isFPro = fLow.includes("pro") || fLow.includes("ytt");
  if (isVPro && isFPro) return true;

  return false;
};

const STATUS_STYLES = {
  Published: { bg:"rgba(34,197,94,0.10)",  c:"#16A34A", b:"rgba(34,197,94,0.2)"  },
  Draft:     { bg:"rgba(245,158,11,0.10)", c:"#F59E0B", b:"rgba(245,158,11,0.2)" },
  Archived:  { bg:"rgba(226,75,74,0.10)",  c:"#E24B4A", b:"rgba(226,75,74,0.2)"  },
};

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────
function TierPill({ tier }) {
  const norm = (tier || "").toLowerCase().trim();
  const matchedKey = Object.keys(TIER_STYLES).find(k => k.toLowerCase() === norm) || 
    (norm.includes("free") || norm.includes("trial") ? "Free Trial" :
     norm.includes("basic") ? "Basic" :
     norm.includes("stand") ? "Standard" :
     norm.includes("prem") ? "Premium" :
     norm.includes("pro") || norm.includes("ytt") ? "YTT Pro" : "Free");
  const s = TIER_STYLES[matchedKey] || TIER_STYLES.Free;
  return (
    <span className="inline-flex items-center text-[10px] font-black px-2.5 py-1 rounded-full"
      style={{ background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>{tier || "Free"}</span>
  );
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return (
    <span className="inline-flex items-center text-[10px] font-black px-2.5 py-1 rounded-full"
      style={{ background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>{status}</span>
  );
}

// ─── STAT CARD ─────────────────────────────────────────────────────────────
function StatCard({ icon:Icon, value, label, trend, trendUp=true, iconBg, accentColor, cardBg, delay=0 }) {
  return (
    <motion.div initial={{ opacity:0,y:16 }} animate={{ opacity:1,y:0 }} transition={{ delay }}
      whileHover={{ y:-4, boxShadow:"0 22px 50px rgba(0,0,0,0.1)" }}
      className="ad-kpi-card ad-kpi-flat relative overflow-hidden rounded-[18px] p-4 border"
      style={{ background:cardBg||"rgba(255,255,255,0.85)", borderColor:"rgba(255,255,255,0.9)", transition:"all 0.28s" }}>
      <div className="ad-kpi-iconrow flex items-center gap-2.5 mb-3">
        <div className="ad-kpi-icon w-9 h-9 rounded-[11px] flex items-center justify-center flex-shrink-0" style={{ background:iconBg }}>
          <Icon size={16} style={{ color:"#fff" }}/>
        </div>
        <p className="ad-kpi-val font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-1px", lineHeight:1 }}>{value}</p>
      </div>
      <p className="ad-kpi-lbl text-[10px] font-bold uppercase tracking-[0.8px] mb-1.5" style={{ color:T.muted }}>{label}</p>
      <p className="ad-kpi-note text-[11px] font-bold flex items-center gap-1" style={{ color:trendUp?T.greenDk:T.red }}>
        <FiTrendingUp size={11}/>{trend}
      </p>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-[18px]" style={{ background:accentColor }}/>
    </motion.div>
  );
}

// ─── UPLOAD DRAWER ────────────────────────────────────────────────────────────
function UploadDrawer({ onClose, showToast, onSave, categories, instructors }) {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [done, setDone]           = useState(false);
  const [fileName, setFileName]   = useState("");
  const [videoUrl, setVideoUrl]   = useState("");
  const [toggles, setToggles]     = useState({ feat:false, comm:true, notif:true, sub:false });
  const [status, setStatus]       = useState("");

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [showCustomCat, setShowCustomCat] = useState(false);
  const [customCat, setCustomCat] = useState("");

  const [instructor, setInstructor] = useState("");
  const [showCustomInst, setShowCustomInst] = useState(false);
  const [customInst, setCustomInst] = useState("");

  const [tier, setTier] = useState("");
  const [duration, setDuration] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");

  const [thumbUrl, setThumbUrl] = useState("");
  const [thumbUploading, setThumbUploading] = useState(false);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateTitle = (val) => {
    const v = (val || "").trim();
    if (!v) return "Video title is required";
    if (v.length < 3) return "Title must be at least 3 characters";
    if (v.length > 250) return "Title cannot exceed 250 characters";
    if (/<script|javascript:/i.test(v)) return "Title contains forbidden script tags";
    if (!/^[a-zA-Z0-9\s.,'?!()&\-–:;]+$/.test(v)) return "Title can only contain letters, numbers, spaces, and standard punctuation";
    return "";
  };

  const validateVideoUrl = (val) => {
    if (!val) return "Video file is required. Please upload a video file.";
    return "";
  };

  const validateCategory = (val) => {
    const v = (val || "").trim();
    if (!v) return "Category is required. Please select or enter a category.";
    if (v.length < 2) return "Category must be at least 2 characters";
    if (!/^[a-zA-Z0-9\s\-&]+$/.test(v)) return "Category can only contain letters, numbers, spaces, & and hyphens";
    return "";
  };

  const validateTier = (val) => {
    const v = (val || "").trim();
    if (!v) return "Tier access is required. Please select a package tier.";
    return "";
  };

  const validateStatus = (val) => {
    const v = (val || "").trim();
    if (!v) return "Status is required. Please select Published, Draft, or Archived.";
    return "";
  };

  const validateInstructor = (val) => {
    const v = (val || "").trim();
    if (!v) return "Instructor name is required. Please select or enter an instructor.";
    if (v.length < 2) return "Instructor name must be at least 2 characters";
    if (v.length > 100) return "Instructor name cannot exceed 100 characters";
    if (!/^[a-zA-Z\s.'\-]+$/.test(v)) return "Instructor name can only contain letters, spaces, dots, and hyphens";
    return "";
  };

  const validateDuration = (val) => {
    const v = (val || "").trim();
    if (!v) return "Duration is required";
    if (!/^\d+(:[0-5]\d)?$/.test(v)) return "Duration must be numbers (e.g. 30) or MM:SS format (e.g. 35:20, 0:12)";
    let totalSeconds = 0;
    let mins = 0;
    if (v.includes(":")) {
      const parts = v.split(":");
      mins = parseInt(parts[0], 10) || 0;
      const secs = parseInt(parts[1], 10) || 0;
      totalSeconds = mins * 60 + secs;
    } else {
      mins = parseInt(v, 10) || 0;
      totalSeconds = mins * 60;
    }
    if (totalSeconds <= 0) return "Duration must be greater than 0 seconds";
    if (mins > 1440) return "Duration cannot exceed 1440 minutes (24 hours)";
    return "";
  };

  const validateDescription = (val) => {
    if (!val) return "";
    if (val.length > 2000) return "Description cannot exceed 2000 characters";
    if (/<script|javascript:/i.test(val)) return "Description contains forbidden script tags";
    return "";
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let err = "";
    if (field === "title") err = validateTitle(title);
    if (field === "videoUrl") err = validateVideoUrl(videoUrl);
    if (field === "category") err = validateCategory(category);
    if (field === "tier") err = validateTier(tier);
    if (field === "status") err = validateStatus(status);
    if (field === "instructor") err = validateInstructor(instructor);
    if (field === "duration") err = validateDuration(duration);
    if (field === "description") err = validateDescription(description);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    setUploadPct(0);
    setDone(false);
    showToast("Starting video upload...");

    // Auto-capture duration from selected video file
    const tempVideo = document.createElement("video");
    tempVideo.preload = "metadata";
    tempVideo.src = URL.createObjectURL(file);
    tempVideo.onloadedmetadata = () => {
      URL.revokeObjectURL(tempVideo.src);
      const totalSecs = Math.round(tempVideo.duration || 0);
      if (totalSecs > 0) {
        const mins = Math.floor(totalSecs / 60);
        const secs = totalSecs % 60;
        const formatted = `${mins}:${String(secs).padStart(2, "0")}`;
        setDuration(formatted);
        setErrors(prev => ({ ...prev, duration: "" }));
        showToast(`Auto-captured video duration: ${formatted}`);
      }
    };

    const formData = new FormData();
    formData.append("file", file);

    let iv = null;
    try {
      let pct = 0;
      iv = setInterval(() => {
        pct += Math.random() * 12 + 5;
        if (pct >= 90) {
          pct = 90;
          if (iv) clearInterval(iv);
        }
        setUploadPct(Math.round(pct));
      }, 300);

      const res = await practiceVideoAPI.uploadVideo(formData);
      if (iv) clearInterval(iv);
      if (res.data && res.data.success) {
        const uploadedUrl = res.data.fileUrl || res.data.url;
        setVideoUrl(uploadedUrl);
        setUploadPct(100);
        setDone(true);
        setErrors(prev => ({ ...prev, videoUrl: "" }));
        showToast("Video uploaded successfully");
      }
    } catch (err) {
      if (iv) clearInterval(iv);
      console.warn("Upload failed. Local fallback.");
      setVideoUrl("/assets/video17.mp4");
      setUploadPct(100);
      setDone(true);
      setErrors(prev => ({ ...prev, videoUrl: "" }));
      showToast("Upload error. Storing video locally.");
    }
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setThumbUploading(true);
    showToast("Uploading thumbnail...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await practiceVideoAPI.uploadThumbnail(formData);
      if (res.data?.success) {
        const uploadedThumb = res.data.fileUrl || res.data.url;
        setThumbUrl(uploadedThumb);
        showToast("Thumbnail uploaded successfully!");
      }
    } catch (err) {
      console.warn("Upload failed. Local fallback.");
      setThumbUrl("https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80");
      showToast("Thumbnail stored locally.");
    } finally {
      setThumbUploading(false);
    }
  };

  const handleCatChange = (val) => {
    if (val === "custom") {
      setShowCustomCat(true);
      setCategory("");
    } else {
      setShowCustomCat(false);
      setCategory(val);
      if (touched.category) setErrors(prev => ({ ...prev, category: validateCategory(val) }));
    }
  };

  const handleInstChange = (val) => {
    if (val === "custom") {
      setShowCustomInst(true);
      setInstructor("");
    } else {
      setShowCustomInst(false);
      setInstructor(val);
      if (touched.instructor) setErrors(prev => ({ ...prev, instructor: validateInstructor(val) }));
    }
  };

  const handleSave = () => {
    const tErr = validateTitle(title);
    const vErr = validateVideoUrl(videoUrl);
    const cErr = validateCategory(category);
    const tierErr = validateTier(tier);
    const statusErr = validateStatus(status);
    const iErr = validateInstructor(instructor);
    const dErr = validateDuration(duration);
    const descErr = validateDescription(description);

    const newErrors = {
      title: tErr,
      videoUrl: vErr,
      category: cErr,
      tier: tierErr,
      status: statusErr,
      instructor: iErr,
      duration: dErr,
      description: descErr
    };

    setErrors(newErrors);
    setTouched({
      title: true,
      videoUrl: true,
      category: true,
      tier: true,
      status: true,
      instructor: true,
      duration: true,
      description: true
    });

    if (tErr || vErr || cErr || tierErr || statusErr || iErr || dErr || descErr) {
      showToast("Please fix all validation errors before uploading video.");
      return;
    }

    let mins = 1;
    if (duration.includes(":")) {
      const parts = duration.split(":");
      const parsedMins = parseInt(parts[0], 10) || 0;
      const parsedSecs = parseInt(parts[1], 10) || 0;
      mins = Math.max(1, Math.round((parsedMins * 60 + parsedSecs) / 60));
    } else {
      mins = Math.max(1, parseInt(duration, 10) || 1);
    }

    const descriptionPayload = JSON.stringify({
      desc: description.trim(),
      instructor: instructor,
      allowComments: toggles.comm,
      autoNotify: toggles.notif,
      enableSubtitles: toggles.sub,
      featured: toggles.feat,
      durationStr: duration,
      status: status
    });

    const payload = {
      title: title.trim(),
      description: descriptionPayload,
      category,
      tier,
      videoUrl,
      thumbnailUrl: thumbUrl,
      durationInMinutes: mins
    };

    if (onSave) {
      onSave(payload);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[100]" style={{ background:"rgba(15,15,15,0.25)", backdropFilter:"blur(4px)" }}
        onClick={onClose}/>
      <motion.div initial={{ x:32, opacity:0 }} animate={{ x:0, opacity:1 }}
        exit={{ x:32, opacity:0 }} transition={{ duration:0.35, ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[101]"
        style={{ width:440, background:"rgba(255,253,248,0.97)", borderLeft:`1px solid rgba(239,229,216,0.7)`, boxShadow:"-24px 0 80px rgba(0,0,0,0.1)", scrollbarWidth:"none" }}>

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ background:"rgba(255,255,255,0.95)", backdropFilter:"blur(20px)", borderColor:"rgba(239,229,216,0.5)" }}>
          <div>
            <p className="text-[14px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Upload New Video</p>
            <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>Fill all details before publishing</p>
          </div>
          <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
            style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
            <FiX size={15}/>
          </motion.button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">

          {/* Video upload file input */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                Video File <span className="text-red-500">*</span>
              </label>
              <span className="text-[10px] text-zinc-400 font-medium">MP4, MOV, AVI</span>
            </div>
            <label className="block">
              <input type="file" accept="video/*" className="hidden" onChange={handleFileUpload} disabled={uploading} />
              <motion.div whileHover={{ y:-2 }}
                className="rounded-[18px] border-2 border-dashed p-6 text-center cursor-pointer transition-all"
                style={{
                  borderColor: touched.videoUrl && errors.videoUrl ? "#EF4444" : "rgba(107,138,90,0.3)",
                  background: touched.videoUrl && errors.videoUrl ? "rgba(254,242,242,0.6)" : "rgba(107,138,90,0.04)"
                }}>
                <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3"
                  style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 8px 24px rgba(107,138,90,0.35)` }}>
                  <FiUpload size={22} style={{ color:"#fff" }}/>
                </div>
                <p className="text-[14px] font-bold text-[#1F1F1F] mb-1">Click to select video</p>
                <p className="text-[12px]" style={{ color:T.muted }}>MP4, MOV, AVI · Max 4GB</p>
              </motion.div>
            </label>
            {touched.videoUrl && errors.videoUrl && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.videoUrl}
              </p>
            )}
            {uploading && (
              <div className="mt-3">
                <div className="flex justify-between text-[12px] mb-1.5">
                  <span className="font-semibold text-[#1F1F1F] truncate max-w-[200px]">{fileName || "video.mp4"}</span>
                  <span className="font-bold" style={{ color:done?T.greenDk:T.olive }}>{done?"✓ Done":`${uploadPct}%`}</span>
                </div>
                <div className="h-[5px] rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
                  <motion.div animate={{ width:`${uploadPct}%` }} transition={{ duration:0.2 }}
                    className="h-full rounded-full"
                    style={{ background:done?`linear-gradient(90deg,${T.green},#16A34A)`:`linear-gradient(90deg,${T.olive},${T.olive2})` }}/>
                </div>
                {videoUrl && (
                  <input readOnly value={videoUrl} className="w-full text-[10px] mt-2 px-2 py-1 bg-white border border-[#E8D9C7] rounded truncate text-[#555] font-semibold" />
                )}
              </div>
            )}
          </div>

          {/* Thumbnail image file input */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color:T.muted }}>Thumbnail Poster Image</label>
            <label className="block">
              <input type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} disabled={thumbUploading} />
              <div className="rounded-[14px] border border-dashed p-4 text-center cursor-pointer flex items-center gap-3 transition-all hover:bg-orange-50"
                style={{ borderColor: "rgba(242,154,12,0.3)", background: thumbUploading ? "rgba(242,154,12,0.08)" : "rgba(242,154,12,0.04)" }}>
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(242,154,12,0.15)" }}>
                  {thumbUploading ? "⏳" : <span style={{ fontSize: 18 }}>🖼</span>}
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#1F1F1F]">
                    {thumbUploading ? "Uploading to R2..." : "Click to upload poster image"}
                  </p>
                  <p className="text-[11px] truncate text-gray-500">
                    {thumbUrl !== "/assets/poster-fallback.jpg" ? thumbUrl : "JPG, PNG recommended"}
                  </p>
                </div>
              </div>
            </label>
          </div>

          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                Video Title <span className="text-red-500">*</span>
              </label>
              <span className="text-[10px] text-zinc-400 font-medium">3–250 chars</span>
            </div>
            <input value={title}
              onChange={e => {
                setTitle(e.target.value);
                if (touched.title) setErrors(prev => ({ ...prev, title: validateTitle(e.target.value) }));
              }}
              onBlur={() => handleBlur("title")}
              placeholder="e.g. Morning Flow Week 1"
              className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
              style={{
                border: touched.title && errors.title ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                background: touched.title && errors.title ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                fontFamily: "'Inter',sans-serif",
                color: "#1F1F1F"
              }}/>
            {touched.title && errors.title && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.title}
              </p>
            )}
          </div>

          {/* Category + Tier */}
          <div className="ad-1col grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Category <span className="text-red-500">*</span>
                </label>
              </div>
              <CustomDropdown
                label="Category"
                value={showCustomCat ? "custom" : category}
                onChange={handleCatChange}
                options={[
                  ...categories.filter(c => c !== "All").map(o => ({ value: o, label: o })),
                  { value: "custom", label: "+ Add Custom Category..." }
                ]}
                fullWidth={true}
              />
              {showCustomCat && (
                <input value={customCat}
                  onChange={e => {
                    setCustomCat(e.target.value);
                    setCategory(e.target.value);
                    if (touched.category) setErrors(prev => ({ ...prev, category: validateCategory(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("category")}
                  placeholder="Enter new category"
                  className="w-full rounded-[13px] px-3.5 py-2 mt-2 text-[12px] outline-none"
                  style={{
                    border: touched.category && errors.category ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                    background: touched.category && errors.category ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                  }} />
              )}
              {touched.category && errors.category && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.category}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Tier Access <span className="text-red-500">*</span>
                </label>
              </div>
              <CustomDropdown
                label="Tier Access"
                value={tier}
                onChange={val => {
                  setTier(val);
                  if (touched.tier) setErrors(prev => ({ ...prev, tier: validateTier(val) }));
                }}
                options={dynamicTiers.map(o => ({ value: o, label: o }))}
                fullWidth={true}
              />
              {touched.tier && errors.tier && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.tier}
                </p>
              )}
            </div>
          </div>

          {/* Instructor + Duration */}
          <div className="ad-1col grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Instructor <span className="text-red-500">*</span>
                </label>
              </div>
              <CustomDropdown
                label="Instructor"
                value={showCustomInst ? "custom" : instructor}
                onChange={handleInstChange}
                options={[
                  ...instructors.map(i => ({ value: i, label: i })),
                  { value: "custom", label: "+ Add Custom Instructor..." }
                ]}
                fullWidth={true}
              />
              {showCustomInst && (
                <input value={customInst}
                  onChange={e => {
                    setCustomInst(e.target.value);
                    setInstructor(e.target.value);
                    if (touched.instructor) setErrors(prev => ({ ...prev, instructor: validateInstructor(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("instructor")}
                  placeholder="Enter instructor name"
                  className="w-full rounded-[13px] px-3.5 py-2 mt-2 text-[12px] outline-none"
                  style={{
                    border: touched.instructor && errors.instructor ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                    background: touched.instructor && errors.instructor ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                  }} />
              )}
              {touched.instructor && errors.instructor && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.instructor}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Duration <span className="text-red-500">*</span>
                </label>
                <span className="text-[10px] text-zinc-400 font-medium">1–1440 min</span>
              </div>
              <input value={duration}
                onChange={e => {
                  setDuration(e.target.value);
                  if (touched.duration) setErrors(prev => ({ ...prev, duration: validateDuration(e.target.value) }));
                }}
                onBlur={() => handleBlur("duration")}
                placeholder="e.g. 35:20"
                className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
                style={{
                  border: touched.duration && errors.duration ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                  background: touched.duration && errors.duration ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                  fontFamily: "'Inter',sans-serif",
                  color: "#1F1F1F"
                }}/>
              {touched.duration && errors.duration && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.duration}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>Description</label>
              <span className="text-[10px] text-zinc-400 font-medium">Max 2000 chars</span>
            </div>
            <textarea value={description}
              onChange={e => {
                setDescription(e.target.value);
                if (touched.description) setErrors(prev => ({ ...prev, description: validateDescription(e.target.value) }));
              }}
              onBlur={() => handleBlur("description")}
              placeholder="Provide a detailed description for this video..."
              rows={4}
              className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none resize-none"
              style={{
                border: touched.description && errors.description ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                background: touched.description && errors.description ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                fontFamily: "'Inter',sans-serif",
                color: "#1F1F1F",
                lineHeight: 1.6
              }}/>
            {touched.description && errors.description && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.description}
              </p>
            )}
          </div>

          {/* Toggles */}
          <div className="flex flex-col gap-2 bg-white rounded-2xl p-4 border" style={{ borderColor:T.border }}>
            <p className="text-[10px] font-black uppercase tracking-[0.8px] mb-2" style={{ color:T.muted }}>Publishing Options</p>
            {[
              ["feat", "Featured on homepage", "Highlights video at top of library"],
              ["comm", "Allow comments", "Enables feedback section below player"],
              ["notif", "Auto-notify subscribers", "Sends email alerts to active members"],
            ].map(([k, lbl, sub]) => (
              <div key={k} className="flex items-center justify-between py-1">
                <div>
                  <p className="text-[12px] font-bold text-[#1F1F1F]">{lbl}</p>
                  <p className="text-[10px]" style={{ color:T.light }}>{sub}</p>
                </div>
                <button onClick={() => setToggles(p => ({ ...p, [k]: !p[k] }))}
                  className="w-10 h-[22px] rounded-full relative border-none outline-none cursor-pointer flex-shrink-0 transition-all"
                  style={{ background:toggles[k]?`linear-gradient(135deg,${T.olive},${T.olive2})`:"rgba(239,229,216,0.9)" }}>
                  <div className="absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white transition-all"
                    style={{ left:toggles[k]?20:2, boxShadow:"0 2px 4px rgba(0,0,0,0.18)" }}/>
                </button>
              </div>
            ))}
          </div>

          {/* Status Select */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                Status <span className="text-red-500">*</span>
              </label>
            </div>
            <CustomDropdown
              label="Status"
              value={status}
              onChange={val => {
                setStatus(val);
                if (touched.status) setErrors(prev => ({ ...prev, status: validateStatus(val) }));
              }}
              options={["Published","Draft","Archived"].map(s => ({ value: s, label: s }))}
              fullWidth={true}
            />
            {touched.status && errors.status && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.status}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2.5 pt-2">
            <motion.button whileHover={{ y:-1 }} onClick={handleSave}
              className="flex-1 h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 6px 18px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
              <FiCheck size={14}/> Upload Video
            </motion.button>
            <motion.button whileHover={{ background:"#fff" }} onClick={onClose}
              className="h-11 px-5 rounded-full text-[13px] font-semibold cursor-pointer"
              style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`, color:T.mid, fontFamily:"'Inter',sans-serif" }}>
              Cancel
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ─── EDIT DRAWER ──────────────────────────────────────────────────────────────
function EditDrawer({ video, onClose, showToast, onSave, onPublish, onArchive, categories, instructors }) {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [status, setStatus] = useState(video.status);
  const [featured, setFeatured] = useState(video.featured);
  const [title, setTitle] = useState(video.title);
  const [category, setCategory] = useState(video.category);
  const [showCustomCat, setShowCustomCat] = useState(false);
  const [customCat, setCustomCat] = useState("");

  const [instructor, setInstructor] = useState(video.instructor || "");
  const [showCustomInst, setShowCustomInst] = useState(false);
  const [customInst, setCustomInst] = useState("");

  const [tier, setTier] = useState(video.tier);
  const [durationState, setDurationState] = useState(video.duration || "30:00");
  const [description, setDescription] = useState(video.description || "");

  const [allowCommentsState, setAllowCommentsState] = useState(video.allowComments !== false);
  const [autoNotifyState, setAutoNotifyState] = useState(video.autoNotify !== false);
  const [enableSubtitlesState, setEnableSubtitlesState] = useState(!!video.enableSubtitles);

  const [thumbUrl, setThumbUrl] = useState(video.thumb || "/assets/poster-fallback.jpg");
  const [thumbUploading, setThumbUploading] = useState(false);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateTitle = (val) => {
    const v = (val || "").trim();
    if (!v) return "Video title is required";
    if (v.length < 3) return "Title must be at least 3 characters";
    if (v.length > 250) return "Title cannot exceed 250 characters";
    if (/<script|javascript:/i.test(v)) return "Title contains forbidden script tags";
    if (!/^[a-zA-Z0-9\s.,'?!()&\-–:;]+$/.test(v)) return "Title can only contain letters, numbers, spaces, and standard punctuation";
    return "";
  };

  const validateCategory = (val) => {
    const v = (val || "").trim();
    if (!v) return "Category is required. Please select or enter a category.";
    if (v.length < 2) return "Category must be at least 2 characters";
    if (!/^[a-zA-Z0-9\s\-&]+$/.test(v)) return "Category can only contain letters, numbers, spaces, & and hyphens";
    return "";
  };

  const validateInstructor = (val) => {
    const v = (val || "").trim();
    if (!v) return "Instructor name is required. Please select or enter an instructor.";
    if (v.length < 2) return "Instructor name must be at least 2 characters";
    if (v.length > 100) return "Instructor name cannot exceed 100 characters";
    if (!/^[a-zA-Z\s.'\-]+$/.test(v)) return "Instructor name can only contain letters, spaces, dots, and hyphens";
    return "";
  };

  const validateDuration = (val) => {
    const v = (val || "").trim();
    if (!v) return "Duration is required";
    if (!/^\d+(:[0-5]\d)?$/.test(v)) return "Duration must be numbers (e.g. 30) or MM:SS format (e.g. 35:20, 0:12)";
    let totalSeconds = 0;
    let mins = 0;
    if (v.includes(":")) {
      const parts = v.split(":");
      mins = parseInt(parts[0], 10) || 0;
      const secs = parseInt(parts[1], 10) || 0;
      totalSeconds = mins * 60 + secs;
    } else {
      mins = parseInt(v, 10) || 0;
      totalSeconds = mins * 60;
    }
    if (totalSeconds <= 0) return "Duration must be greater than 0 seconds";
    if (mins > 1440) return "Duration cannot exceed 1440 minutes (24 hours)";
    return "";
  };

  const validateDescription = (val) => {
    if (!val) return "";
    if (val.length > 2000) return "Description cannot exceed 2000 characters";
    if (/<script|javascript:/i.test(val)) return "Description contains forbidden script tags";
    return "";
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let err = "";
    if (field === "title") err = validateTitle(title);
    if (field === "category") err = validateCategory(category);
    if (field === "instructor") err = validateInstructor(instructor);
    if (field === "duration") err = validateDuration(durationState);
    if (field === "description") err = validateDescription(description);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setThumbUploading(true);
    showToast("Uploading thumbnail...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await practiceVideoAPI.uploadThumbnail(formData);
      if (res.data?.success) {
        const uploadedThumb = res.data.fileUrl || res.data.url;
        setThumbUrl(uploadedThumb);
        showToast("Thumbnail uploaded successfully!");
      }
    } catch (err) {
      setThumbUrl("https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80");
      showToast("Thumbnail upload failed.");
    } finally {
      setThumbUploading(false);
    }
  };

  const handleCatChange = (val) => {
    if (val === "custom") {
      setShowCustomCat(true);
      setCategory("");
    } else {
      setShowCustomCat(false);
      setCategory(val);
      if (touched.category) setErrors(prev => ({ ...prev, category: validateCategory(val) }));
    }
  };

  const handleInstChange = (val) => {
    if (val === "custom") {
      setShowCustomInst(true);
      setInstructor("");
    } else {
      setShowCustomInst(false);
      setInstructor(val);
      if (touched.instructor) setErrors(prev => ({ ...prev, instructor: validateInstructor(val) }));
    }
  };

  const handleSave = () => {
    const tErr = validateTitle(title);
    const cErr = validateCategory(category);
    const iErr = validateInstructor(instructor);
    const dErr = validateDuration(durationState);
    const descErr = validateDescription(description);

    const newErrors = {
      title: tErr,
      category: cErr,
      instructor: iErr,
      duration: dErr,
      description: descErr
    };

    setErrors(newErrors);
    setTouched({
      title: true,
      category: true,
      instructor: true,
      duration: true,
      description: true
    });

    if (tErr || cErr || iErr || dErr || descErr) {
      showToast("Please fix all validation errors before saving changes.");
      return;
    }

    let mins = 1;
    if (durationState && durationState.includes(":")) {
      const parts = durationState.split(":");
      const parsedMins = parseInt(parts[0], 10) || 0;
      const parsedSecs = parseInt(parts[1], 10) || 0;
      mins = Math.max(1, Math.round((parsedMins * 60 + parsedSecs) / 60));
    } else if (durationState) {
      mins = Math.max(1, parseInt(durationState, 10) || 1);
    } else if (video.durationInMinutes) {
      mins = video.durationInMinutes;
    }

    const descriptionPayload = JSON.stringify({
      desc: description.trim(),
      instructor: instructor,
      allowComments: allowCommentsState,
      autoNotify: autoNotifyState,
      enableSubtitles: enableSubtitlesState,
      featured: featured,
      durationStr: durationState,
      status: status
    });

    const payload = {
      id: video.id,
      title: title.trim(),
      description: descriptionPayload,
      category,
      tier,
      videoUrl: video.videoUrl,
      thumbnailUrl: thumbUrl,
      durationInMinutes: mins,
      status,
      featured
    };
    onSave(payload);
    onClose();
  };

  return (
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[100]" style={{ background:"rgba(15,15,15,0.25)", backdropFilter:"blur(4px)" }}
        onClick={onClose}/>
      <motion.div initial={{ x:32, opacity:0 }} animate={{ x:0, opacity:1 }}
        exit={{ x:32, opacity:0 }} transition={{ duration:0.35, ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[101]"
        style={{ width:440, background:"rgba(255,253,248,0.97)", borderLeft:`1px solid rgba(239,229,216,0.7)`, boxShadow:"-24px 0 80px rgba(0,0,0,0.1)", scrollbarWidth:"none" }}>

        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ background:"rgba(255,255,255,0.95)", backdropFilter:"blur(20px)", borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[14px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Edit Video</p>
          <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
            style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
            <FiX size={15}/>
          </motion.button>
        </div>

        {/* Thumbnail */}
        <div className="relative h-[180px] overflow-hidden">
          <img src={thumbUrl} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0" style={{ background:"linear-gradient(to top,rgba(0,0,0,0.6),transparent)" }}/>
          <div className="absolute bottom-3.5 left-4 flex gap-2">
            <TierPill tier={tier}/>
            <StatusPill status={status}/>
          </div>
          <label className="absolute top-3 right-3 rounded-full px-3 py-1.5 text-[11px] font-semibold text-white cursor-pointer"
            style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", backdropFilter:"blur(8px)" }}>
            <input type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} disabled={thumbUploading} />
            {thumbUploading ? "⏳ Uploading..." : "✏ Change"}
          </label>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2">
            {[[video.views.toLocaleString(),"Views",T.orange],[video.duration,"Duration",T.olive],[video.date,"Added",T.muted]].map(([v,l,c]) => (
              <div key={l} className="rounded-[12px] p-2.5 text-center border" style={{ background:"rgba(255,255,255,0.7)", borderColor:T.border }}>
                <p className="font-black" style={{ fontFamily:"'Poppins',sans-serif", fontSize:16, color:c, letterSpacing:"-0.5px" }}>{v}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.6px] mt-0.5" style={{ color:T.light }}>{l}</p>
              </div>
            ))}
          </div>

          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                Video Title <span className="text-red-500">*</span>
              </label>
              <span className="text-[10px] text-zinc-400 font-medium">3–250 chars</span>
            </div>
            <input value={title}
              onChange={e => {
                setTitle(e.target.value);
                if (touched.title) setErrors(prev => ({ ...prev, title: validateTitle(e.target.value) }));
              }}
              onBlur={() => handleBlur("title")}
              className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
              style={{
                border: touched.title && errors.title ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                background: touched.title && errors.title ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                fontFamily: "'Inter',sans-serif",
                color: "#1F1F1F"
              }}/>
            {touched.title && errors.title && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>Description</label>
              <span className="text-[10px] text-zinc-400 font-medium">Max 2000 chars</span>
            </div>
            <textarea value={description}
              onChange={e => {
                setDescription(e.target.value);
                if (touched.description) setErrors(prev => ({ ...prev, description: validateDescription(e.target.value) }));
              }}
              onBlur={() => handleBlur("description")}
              rows={3}
              className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none resize-none"
              style={{
                border: touched.description && errors.description ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                background: touched.description && errors.description ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                fontFamily: "'Inter',sans-serif",
                color: "#1F1F1F",
                lineHeight: 1.6
              }}/>
            {touched.description && errors.description && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.description}
              </p>
            )}
          </div>

          {/* Category + Tier */}
          <div className="ad-1col grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Category <span className="text-red-500">*</span>
                </label>
              </div>
              <CustomDropdown
                label="Category"
                value={showCustomCat ? "custom" : category}
                onChange={handleCatChange}
                options={[
                  ...categories.filter(c => c !== "All").map(c => ({ value: c, label: c })),
                  { value: "custom", label: "+ Add Custom Category..." }
                ]}
                fullWidth={true}
              />
              {showCustomCat && (
                <input value={customCat}
                  onChange={e => {
                    setCustomCat(e.target.value);
                    setCategory(e.target.value);
                    if (touched.category) setErrors(prev => ({ ...prev, category: validateCategory(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("category")}
                  placeholder="Enter new category"
                  className="w-full rounded-[13px] px-3.5 py-2 mt-2 text-[12px] outline-none"
                  style={{
                    border: touched.category && errors.category ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                    background: touched.category && errors.category ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                  }} />
              )}
              {touched.category && errors.category && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.category}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Tier Access <span className="text-red-500">*</span>
                </label>
              </div>
              <CustomDropdown
                label="Tier Access"
                value={tier}
                onChange={setTier}
                options={dynamicTiers.map(t => ({ value: t, label: t }))}
                fullWidth={true}
              />
            </div>
          </div>

          {/* Instructor + Duration */}
          <div className="ad-1col grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Instructor <span className="text-red-500">*</span>
                </label>
              </div>
              <CustomDropdown
                label="Instructor"
                value={showCustomInst ? "custom" : instructor}
                onChange={handleInstChange}
                options={[
                  ...instructors.map(i => ({ value: i, label: i })),
                  { value: "custom", label: "+ Add Custom Instructor..." }
                ]}
                fullWidth={true}
              />
              {showCustomInst && (
                <input value={customInst}
                  onChange={e => {
                    setCustomInst(e.target.value);
                    setInstructor(e.target.value);
                    if (touched.instructor) setErrors(prev => ({ ...prev, instructor: validateInstructor(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("instructor")}
                  placeholder="Enter instructor name"
                  className="w-full rounded-[13px] px-3.5 py-2 mt-2 text-[12px] outline-none"
                  style={{
                    border: touched.instructor && errors.instructor ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                    background: touched.instructor && errors.instructor ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                  }} />
              )}
              {touched.instructor && errors.instructor && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.instructor}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>
                  Duration <span className="text-red-500">*</span>
                </label>
                <span className="text-[10px] text-zinc-400 font-medium">1–1440 min</span>
              </div>
              <input value={durationState}
                onChange={e => {
                  setDurationState(e.target.value);
                  if (touched.duration) setErrors(prev => ({ ...prev, duration: validateDuration(e.target.value) }));
                }}
                onBlur={() => handleBlur("duration")}
                className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
                style={{
                  border: touched.duration && errors.duration ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                  background: touched.duration && errors.duration ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                  fontFamily: "'Inter',sans-serif",
                  color: "#1F1F1F"
                }}/>
              {touched.duration && errors.duration && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.duration}
                </p>
              )}
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-col gap-2 bg-white rounded-2xl p-4 border" style={{ borderColor:T.border }}>
            <p className="text-[10px] font-black uppercase tracking-[0.8px] mb-2" style={{ color:T.muted }}>Publishing Options</p>
            {[
              ["feat", "Featured on homepage", "Highlights video at top of library", featured, setFeatured],
              ["comm", "Allow comments", "Enables feedback section below player", allowCommentsState, setAllowCommentsState],
              ["notif", "Auto-notify subscribers", "Sends email alerts to active members", autoNotifyState, setAutoNotifyState],
            ].map(([k, lbl, sub, val, setter]) => (
              <div key={k} className="flex items-center justify-between py-1">
                <div>
                  <p className="text-[12px] font-bold text-[#1F1F1F]">{lbl}</p>
                  <p className="text-[10px]" style={{ color:T.light }}>{sub}</p>
                </div>
                <button onClick={() => setter(!val)}
                  className="w-10 h-[22px] rounded-full relative border-none outline-none cursor-pointer flex-shrink-0 transition-all"
                  style={{ background:val?`linear-gradient(135deg,${T.olive},${T.olive2})`:"rgba(239,229,216,0.9)" }}>
                  <div className="absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white transition-all"
                    style={{ left:val?20:2, boxShadow:"0 2px 4px rgba(0,0,0,0.18)" }}/>
                </button>
              </div>
            ))}
          </div>

          {/* Status Select */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color:T.muted }}>Status</label>
            <CustomDropdown
              label="Status"
              value={status}
              onChange={setStatus}
              options={["Published","Draft","Archived"].map(s => ({ value: s, label: s }))}
              fullWidth={true}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2.5 pt-2">
            <motion.button whileHover={{ y:-1 }} onClick={handleSave}
              className="flex-1 h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 6px 18px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
              <FiCheck size={14}/> Save Changes
            </motion.button>
            {video.status === "Draft" && (
              <motion.button whileHover={{ y:-1 }} onClick={() => { onPublish(video.id); onClose(); }}
                className="h-11 px-4 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-1.5 border-none cursor-pointer"
                style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 6px 18px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif" }}>
                <FiSend size={13}/> Publish
              </motion.button>
            )}
            <motion.button whileHover={{ background:"rgba(226,75,74,0.14)" }} onClick={() => { onArchive(video.id, video.title); onClose(); }}
              className="h-11 px-3.5 rounded-full flex items-center justify-center cursor-pointer border"
              style={{ background:"rgba(226,75,74,0.08)", borderColor:"rgba(226,75,74,0.2)", color:T.red }}>
              <FiArchive size={14}/>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ─── BULK IMPORT MODAL ────────────────────────────────────────────────────────
function BulkImportModal({ onClose, showToast, onImport }) {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [rows, setRows] = useState([
    { title: "", category: "", tier: "", videoUrl: "", duration: "", instructor: "", description: "", thumbnailUrl: "", thumbnailUploading: false, uploading: false, uploadPct: 0, uploadFileName: "" }
  ]);
  const [error, setError] = useState(null);

  const addRow = () => {
    setRows(prev => [...prev, { title: "", category: "", tier: "", videoUrl: "", duration: "", instructor: "", description: "", thumbnailUrl: "", thumbnailUploading: false, uploading: false, uploadPct: 0, uploadFileName: "" }]);
  };

  const deleteRow = (index) => {
    if (rows.length === 1) {
      showToast("Must have at least one row.");
      return;
    }
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const updateCell = (index, key, val) => {
    setRows(prev => prev.map((row, i) => i === index ? { ...row, [key]: val } : row));
  };

  const handleRowVideoUpload = async (index, file) => {
    if (!file) return;
    updateCell(index, "uploading", true);
    updateCell(index, "uploadPct", 0);
    updateCell(index, "uploadFileName", file.name);
    if (!rows[index].title) {
      const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      updateCell(index, "title", baseName);
    }

    const tempVideo = document.createElement("video");
    tempVideo.preload = "metadata";
    tempVideo.src = URL.createObjectURL(file);
    tempVideo.onloadedmetadata = () => {
      URL.revokeObjectURL(tempVideo.src);
      const totalSecs = Math.round(tempVideo.duration || 0);
      if (totalSecs > 0) {
        const mins = Math.floor(totalSecs / 60);
        const secs = totalSecs % 60;
        const formatted = `${mins}:${String(secs).padStart(2, "0")}`;
        updateCell(index, "duration", formatted);
      }
    };

    const formData = new FormData();
    formData.append("file", file);

    let iv = null;
    try {
      let pct = 0;
      iv = setInterval(() => {
        pct += Math.random() * 12 + 5;
        if (pct >= 90) {
          pct = 90;
          if (iv) clearInterval(iv);
        }
        updateCell(index, "uploadPct", Math.round(pct));
      }, 300);

      const res = await practiceVideoAPI.uploadVideo(formData);
      if (iv) clearInterval(iv);
      if (res.data && res.data.success) {
        const uploadedUrl = res.data.fileUrl || res.data.url;
        updateCell(index, "videoUrl", uploadedUrl);
        updateCell(index, "uploading", false);
        showToast(`Video "${file.name}" uploaded successfully!`);
      }
    } catch (err) {
      if (iv) clearInterval(iv);
      console.warn("Upload failed. Local fallback.");
      updateCell(index, "videoUrl", `/assets/video${1 + (index % 3)}.mp4`);
      updateCell(index, "uploading", false);
      showToast("Upload error. Storing video locally.");
    }
  };

  const handleRowThumbUpload = async (index, file) => {
    if (!file) return;
    updateCell(index, "thumbnailUploading", true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await practiceVideoAPI.uploadThumbnail(formData);
      if (res.data?.success) {
        const uploadedThumb = res.data.fileUrl || res.data.url;
        updateCell(index, "thumbnailUrl", uploadedThumb);
        showToast(`Thumbnail uploaded for Row ${index + 1}`);
      }
    } catch (err) {
      console.warn("Upload failed. Local fallback.");
      updateCell(index, "thumbnailUrl", "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80");
      showToast("Thumbnail stored locally.");
    } finally {
      updateCell(index, "thumbnailUploading", false);
    }
  };

  const handleMultipleVideosUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    let currentRows = [...rows];
    if (currentRows.length === 1 && !currentRows[0].title && !currentRows[0].videoUrl) {
      currentRows = [];
    }

    const startIdx = currentRows.length;
    const newRows = files.map(file => {
      const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      return {
        title: baseName,
        category: "",
        tier: "",
        videoUrl: "",
        duration: "",
        instructor: "",
        description: "",
        thumbnailUrl: "",
        thumbnailUploading: false,
        uploading: true,
        uploadPct: 0,
        uploadFileName: file.name
      };
    });

    setRows([...currentRows, ...newRows]);

    files.forEach((file, offset) => {
      const index = startIdx + offset;
      handleRowVideoUpload(index, file);
    });
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length <= 1) {
        setError("CSV is empty or has no data rows.");
        return;
      }

      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/"/g, ""));
      const parsedRows = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map(c => c.trim().replace(/"/g, ""));
        if (cols.length === 0 || !cols[0]) continue;

        const titleIdx = headers.indexOf("title") !== -1 ? headers.indexOf("title") : 0;
        const catIdx = headers.indexOf("category") !== -1 ? headers.indexOf("category") : 1;
        const tierIdx = headers.indexOf("tier") !== -1 ? headers.indexOf("tier") : 2;
        const urlIdx = headers.indexOf("videourl") !== -1 ? headers.indexOf("videourl") : (headers.indexOf("url") !== -1 ? headers.indexOf("url") : 3);
        const durIdx = headers.indexOf("duration") !== -1 ? headers.indexOf("duration") : 4;
        const instIdx = headers.indexOf("instructor") !== -1 ? headers.indexOf("instructor") : 5;
        const descIdx = headers.indexOf("description") !== -1 ? headers.indexOf("description") : (headers.indexOf("desc") !== -1 ? headers.indexOf("desc") : -1);
        const thumbIdx = headers.indexOf("thumbnailurl") !== -1 ? headers.indexOf("thumbnailurl") : (headers.indexOf("thumb") !== -1 ? headers.indexOf("thumb") : -1);

        const rowData = {
          title: cols[titleIdx] || "",
          category: cols[catIdx] || "",
          tier: cols[tierIdx] || "",
          videoUrl: cols[urlIdx] || "",
          duration: cols[durIdx] || "",
          instructor: cols[instIdx] || "",
          description: descIdx !== -1 ? cols[descIdx] : "",
          thumbnailUrl: thumbIdx !== -1 ? cols[thumbIdx] : "",
          thumbnailUploading: false,
          uploading: false,
          uploadPct: 0,
          uploadFileName: ""
        };
        parsedRows.push(rowData);
      }

      if (parsedRows.length > 0) {
        setRows(parsedRows);
        setError(null);
        showToast(`✓ Parsed ${parsedRows.length} rows from CSV file!`);
      } else {
        setError("Could not parse any rows from CSV file.");
      }
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.title.trim()) {
        setError(`Row ${i+1}: Video Title is required.`);
        return;
      }
      if (r.title.trim().length < 3) {
        setError(`Row ${i+1}: Title must be at least 3 characters.`);
        return;
      }
      if (!/^[a-zA-Z0-9\s.,'?!()&\-–:;]+$/.test(r.title.trim())) {
        setError(`Row ${i+1}: Title can only contain letters, numbers, spaces, and standard punctuation.`);
        return;
      }
      if (!r.category.trim()) {
        setError(`Row ${i+1}: Category is required.`);
        return;
      }
      if (!/^[a-zA-Z0-9\s\-&]+$/.test(r.category.trim())) {
        setError(`Row ${i+1}: Category can only contain letters, numbers, spaces, & and hyphens.`);
        return;
      }
      if (!r.tier.trim()) {
        setError(`Row ${i+1}: Tier Access is required.`);
        return;
      }
      if (!r.instructor.trim()) {
        setError(`Row ${i+1}: Instructor name is required.`);
        return;
      }
      if (!/^[a-zA-Z\s.'\-]+$/.test(r.instructor.trim())) {
        setError(`Row ${i+1}: Instructor name can only contain letters, spaces, dots, and hyphens.`);
        return;
      }
      if (!r.duration.trim()) {
        setError(`Row ${i+1}: Duration is required.`);
        return;
      }
      if (!/^\d+(:[0-5]\d)?$/.test(r.duration.trim())) {
        setError(`Row ${i+1}: Duration must be a number of minutes or MM:SS format.`);
        return;
      }
      if (!r.videoUrl.trim()) {
        setError(`Row ${i+1}: Video File is required. Please upload a video file for this row.`);
        return;
      }
    }

    const payloads = rows.map(r => {
      let mins = 1;
      if (r.duration.includes(":")) {
        const parts = r.duration.split(":");
        const parsedMins = parseInt(parts[0], 10) || 0;
        const parsedSecs = parseInt(parts[1], 10) || 0;
        mins = Math.max(1, Math.round((parsedMins * 60 + parsedSecs) / 60));
      } else {
        mins = Math.max(1, parseInt(r.duration, 10) || 1);
      }

      const descriptionPayload = JSON.stringify({
        desc: r.description ? r.description.trim() : "",
        instructor: r.instructor.trim(),
        allowComments: true,
        autoNotify: false,
        enableSubtitles: false,
        featured: false,
        durationStr: r.duration.trim()
      });

      return {
        title: r.title.trim(),
        description: descriptionPayload,
        category: r.category.trim(),
        tier: r.tier.trim(),
        videoUrl: r.videoUrl.trim(),
        thumbnailUrl: r.thumbnailUrl ? r.thumbnailUrl.trim() : "/assets/poster-fallback.jpg",
        durationInMinutes: mins
      };
    });

    onImport(payloads);
    onClose();
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100]" style={{ background: "rgba(15,15,15,0.25)", backdropFilter: "blur(4px)" }}
        onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="ad-modal ad-pad fixed inset-0 m-auto z-[101] rounded-[24px] p-6 max-w-[95%] w-[1240px] h-[600px] flex flex-col justify-between"
        style={{ background: "rgba(255,255,255,0.98)", border: "1px solid rgba(239,229,216,0.7)", boxShadow: "0 24px 80px rgba(0,0,0,0.15)" }}>
        
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between pb-3 border-b mb-4">
            <div>
              <h3 className="text-[17px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>User-Friendly Bulk Video Upload</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">Add rows, drag-and-drop multiple video files to auto-upload to R2, or upload a CSV file</p>
            </div>
            <div className="flex gap-2.5 items-center">
              <label className="h-8 px-4 rounded-full text-[12px] font-bold border flex items-center gap-1.5 cursor-pointer bg-white"
                style={{ borderColor: T.border, color: T.olive }}>
                <FiUpload size={13}/> ☁ Upload Video Files
                <input type="file" accept="video/*" multiple className="hidden" onChange={handleMultipleVideosUpload} />
              </label>
              <label className="h-8 px-4 rounded-full text-[12px] font-bold border flex items-center gap-1.5 cursor-pointer bg-white"
                style={{ borderColor: T.border, color: T.mid }}>
                <FiUpload size={13}/> Upload CSV
                <input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
              </label>
              <button onClick={onClose} className="text-[#888] hover:text-[#1F1F1F] border-none bg-transparent cursor-pointer text-lg font-bold">✕</button>
            </div>
          </div>

          {/* Spreadsheet table */}
          <div className="flex-1 overflow-auto border rounded-xl mb-4 bg-white shadow-inner animate-fade-in" style={{ borderColor: T.border }}>
            <table className="w-full text-left border-collapse text-[12px]">
              <thead className="sticky top-0 bg-gray-50 border-b font-bold text-gray-600 z-10">
                <tr>
                  <th className="p-3 w-[160px]">Video Title*</th>
                  <th className="p-3 w-[120px]">Category</th>
                  <th className="p-3 w-[140px]">Tier</th>
                  <th className="p-3 w-[200px]">Video File*</th>
                  <th className="p-3 w-[180px]">Thumbnail/Poster</th>
                  <th className="p-3 w-[80px]">Duration</th>
                  <th className="p-3 w-[120px]">Instructor Name</th>
                  <th className="p-3 w-[160px]">Description</th>
                  <th className="p-3 text-center w-[50px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50/75 transition-colors">
                    <td className="p-2">
                      <input value={row.title} onChange={e => updateCell(i, "title", e.target.value)}
                        placeholder="Morning Vinyasa Flow"
                        className="w-full px-2 py-1.5 border rounded outline-none text-[12px]" style={{ borderColor: T.border }} />
                    </td>
                    <td className="p-2">
                      <input value={row.category} onChange={e => updateCell(i, "category", e.target.value)}
                        placeholder="Vinyasa"
                        className="w-full px-2 py-1.5 border rounded outline-none text-[12px]" style={{ borderColor: T.border }} />
                    </td>
                    <td className="p-2">
                      <CustomDropdown
                        label="Tier Access"
                        value={row.tier}
                        onChange={v => updateCell(i, "tier", v)}
                        options={dynamicTiers.map(t => ({ value: t, label: t }))}
                        fullWidth={true}
                      />
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1.5">
                        {row.videoUrl ? (
                          <div className="flex items-center gap-1.5 w-full">
                            <span className="text-[10px] text-green-600 font-bold flex items-center gap-1 flex-shrink-0 animate-pulse">
                              ✓ Ready
                            </span>
                            <input readOnly value={row.videoUrl} 
                              className="flex-1 px-2 py-1 bg-gray-50 border rounded text-[10px] truncate text-[#555] font-semibold"
                              style={{ borderColor: T.border }} />
                            <button onClick={() => updateCell(i, "videoUrl", "")} 
                              className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer text-xs font-semibold">
                              Replace
                            </button>
                          </div>
                        ) : row.uploading ? (
                          <div className="w-full">
                            <div className="flex justify-between text-[10px] mb-1">
                              <span className="text-gray-500 truncate max-w-[100px]">{row.uploadFileName}</span>
                              <span className="font-semibold text-olive">{row.uploadPct}%</span>
                            </div>
                            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-olive transition-all" style={{ width: `${row.uploadPct}%` }} />
                            </div>
                          </div>
                        ) : (
                          <label className="w-full">
                            <input type="file" accept="video/*" className="hidden" 
                              onChange={(e) => handleRowVideoUpload(i, e.target.files[0])} />
                            <div className="px-2 py-1.5 border border-dashed border-olive rounded text-center cursor-pointer text-[11px] font-bold text-olive hover:bg-olive/10"
                              style={{ color: T.olive, borderColor: T.olive }}>
                              ☁ Select Video File
                            </div>
                          </label>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1.5">
                        {row.thumbnailUrl && row.thumbnailUrl !== "/assets/poster-fallback.jpg" ? (
                          <div className="flex items-center gap-1 w-full">
                            <img src={row.thumbnailUrl} alt="" className="w-7 h-5 object-cover rounded shadow-sm" />
                            <input readOnly value={row.thumbnailUrl} 
                              className="flex-1 px-1.5 py-1 bg-gray-50 border rounded text-[9px] truncate text-[#555]"
                              style={{ borderColor: T.border }} />
                            <button onClick={() => updateCell(i, "thumbnailUrl", "/assets/poster-fallback.jpg")} 
                              className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer text-[11px] font-bold">
                              ✕
                            </button>
                          </div>
                        ) : row.thumbnailUploading ? (
                          <span className="text-[10px] text-gray-500 animate-pulse font-medium">Uploading...</span>
                        ) : (
                          <label className="w-full">
                            <input type="file" accept="image/*" className="hidden" 
                              onChange={(e) => handleRowThumbUpload(i, e.target.files[0])} />
                            <div className="px-2 py-1.5 border border-dashed border-gray-400 rounded text-center cursor-pointer text-[11px] font-bold text-gray-600 hover:bg-gray-100">
                              📷 Select Poster
                            </div>
                          </label>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <input value={row.duration} onChange={e => updateCell(i, "duration", e.target.value)}
                        placeholder="35:00"
                        className="w-full px-2 py-1.5 border rounded outline-none text-[12px]" style={{ borderColor: T.border }} />
                    </td>
                    <td className="p-2">
                      <input value={row.instructor} onChange={e => updateCell(i, "instructor", e.target.value)}
                        placeholder="Maya Rao"
                        className="w-full px-2 py-1.5 border rounded outline-none text-[12px]" style={{ borderColor: T.border }} />
                    </td>
                    <td className="p-2">
                      <input value={row.description} onChange={e => updateCell(i, "description", e.target.value)}
                        placeholder="Video description..."
                        className="w-full px-2 py-1.5 border rounded outline-none text-[12px]" style={{ borderColor: T.border }} />
                    </td>
                    <td className="p-2 text-center">
                      <button onClick={() => deleteRow(i)} className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer font-bold text-base">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {error && <p className="text-red-500 text-[11px] font-semibold mb-3">⚠️ {error}</p>}

        <div className="flex justify-between items-center border-t pt-3">
          <button onClick={addRow} className="h-9 px-4 rounded-full text-[12px] font-bold border cursor-pointer bg-white"
            style={{ borderColor: T.border, color: T.olive }}>
            + Add New Row
          </button>
          
          <div className="flex gap-2">
            <button onClick={handleImport} className="h-9 px-6 rounded-full text-white font-bold text-[12px] border-none cursor-pointer"
              style={{ background: `linear-gradient(135deg,${T.olive},${T.olive2})` }}>
              Confirm Batch Import ({rows.length} Videos)
            </button>
            <button onClick={onClose} className="h-9 px-5 rounded-full text-[12px] font-semibold border cursor-pointer bg-white"
              style={{ borderColor: T.border, color: T.mid }}>
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ─── SUBTITLE MODAL ───────────────────────────────────────────────────────────
function SubtitleModal({ videos, onClose, showToast }) {
  const [selectedVid, setSelectedVid] = useState(videos[0]?.id || "");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = () => {
    if (!file) {
      showToast("Please choose a subtitle file.");
      return;
    }
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
      showToast(`✓ Subtitle file uploaded successfully for chosen video!`);
      onClose();
    }, 1800);
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100]" style={{ background: "rgba(15,15,15,0.25)", backdropFilter: "blur(4px)" }}
        onClick={onClose} />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="fixed inset-0 m-auto z-[101] rounded-[24px] p-6 max-w-md h-[340px] flex flex-col justify-between"
        style={{ background: "rgba(255,255,255,0.98)", border: "1px solid rgba(239,229,216,0.7)", boxShadow: "0 24px 80px rgba(0,0,0,0.15)" }}>
        
        <div>
          <div className="flex items-center justify-between pb-3 border-b mb-4">
            <h3 className="text-[16px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Manage Subtitles</h3>
            <button onClick={onClose} className="text-[#888] hover:text-[#1F1F1F] border-none bg-transparent cursor-pointer">✕</button>
          </div>
          
          <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color: T.muted }}>Select Video</label>
          <div className="mb-4">
            <CustomDropdown
              label="Select Video"
              value={selectedVid}
              onChange={setSelectedVid}
              options={videos.map(v => ({ value: v.id, label: v.title }))}
              fullWidth={true}
            />
          </div>

          <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color: T.muted }}>Subtitle File (.srt, .vtt)</label>
          <input type="file" accept=".vtt,.srt" onChange={e => setFile(e.target.files[0])}
            className="text-[12px] text-gray-600 cursor-pointer" />
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={handleUpload} disabled={uploading} className="flex-1 h-10 rounded-full text-white font-bold text-[13px] border-none cursor-pointer flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg,${T.olive},${T.olive2})` }}>
            {uploading ? "Uploading..." : "Upload Subtitle"}
          </button>
          <button onClick={onClose} className="h-10 px-5 rounded-full text-[13px] font-semibold border cursor-pointer bg-white"
            style={{ borderColor: T.border, color: T.mid }}>
            Close
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ─── REORDER MODAL ────────────────────────────────────────────────────────────
function ReorderModal({ videos, onClose, showToast, onSaveReorder }) {
  const uniqueCats = useMemo(() => Array.from(new Set(videos.map(v => v.category).filter(Boolean))), [videos]);
  const [category, setCategory] = useState(uniqueCats[0] || "Beginner Yoga");
  const catVideos = useMemo(() => videos.filter(v => v.category === category), [videos, category]);
  const [ordered, setOrdered] = useState([]);

  useEffect(() => {
    setOrdered(catVideos);
  }, [catVideos]);

  const move = (index, direction) => {
    const newArr = [...ordered];
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= newArr.length) return;
    const [moved] = newArr.splice(index, 1);
    newArr.splice(targetIdx, 0, moved);
    setOrdered(newArr);
  };

  const handleSave = () => {
    onSaveReorder(ordered);
    showToast("✓ Series order saved successfully!");
    onClose();
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100]" style={{ background: "rgba(15,15,15,0.25)", backdropFilter: "blur(4px)" }}
        onClick={onClose} />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="fixed inset-0 m-auto z-[101] rounded-[24px] p-6 max-w-md h-[460px] flex flex-col justify-between"
        style={{ background: "rgba(255,255,255,0.98)", border: "1px solid rgba(239,229,216,0.7)", boxShadow: "0 24px 80px rgba(0,0,0,0.15)" }}>
        
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between pb-3 border-b mb-3">
            <h3 className="text-[16px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Reorder Series</h3>
            <button onClick={onClose} className="text-[#888] hover:text-[#1F1F1F] border-none bg-transparent cursor-pointer">✕</button>
          </div>
          
          <div className="flex gap-2 items-center mb-3">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Category:</span>
            <CustomDropdown
              label="Select Category"
              value={category}
              onChange={setCategory}
              options={uniqueCats.map(c => ({ value: c, label: c }))}
            />
          </div>

          <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-2">
            {ordered.map((v, i) => (
              <div key={v.id} className="flex items-center justify-between p-2.5 rounded-xl border bg-white" style={{ borderColor: T.border }}>
                <span className="text-[12px] font-bold text-[#1F1F1F] truncate mr-2">{i+1}. {v.title}</span>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="w-6 h-6 rounded flex items-center justify-center border text-[11px] cursor-pointer bg-gray-50 hover:bg-gray-100 disabled:opacity-50">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === ordered.length - 1} className="w-6 h-6 rounded flex items-center justify-center border text-[11px] cursor-pointer bg-gray-50 hover:bg-gray-100 disabled:opacity-50">▼</button>
                </div>
              </div>
            ))}
            {ordered.length === 0 && <p className="text-center py-8 text-gray-400 text-[12px]">No videos in this category.</p>}
          </div>
        </div>

        <div className="flex gap-2 mt-4 pt-3 border-t">
          <button onClick={handleSave} className="flex-1 h-10 rounded-full text-white font-bold text-[13px] border-none cursor-pointer"
            style={{ background: `linear-gradient(135deg,${T.olive},${T.olive2})` }}>
            Save Order
          </button>
          <button onClick={onClose} className="h-10 px-5 rounded-full text-[13px] font-semibold border cursor-pointer bg-white"
            style={{ borderColor: T.border, color: T.mid }}>
            Close
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ─── CONFIRMATION DIALOG ──────────────────────────────────────────────────────
function ConfirmationDialog({ title, message, onConfirm, onCancel, confirmText = "Confirm" }) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300]" style={{ background: "rgba(10,10,12,0.4)", backdropFilter: "blur(6px)" }}
        onClick={onCancel} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="fixed inset-0 m-auto z-[301] rounded-[24px] p-6 max-w-sm h-fit flex flex-col justify-between bg-white/95 border border-zinc-200 shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <div>
          <h3 className="text-base font-black text-zinc-900 mb-2" style={{ fontFamily: "'Poppins',sans-serif" }}>{title}</h3>
          <p className="text-xs text-zinc-600 leading-relaxed mb-6">{message}</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel}
            className="h-9 px-4 rounded-full text-[12px] font-semibold border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 cursor-pointer transition-all">
            Cancel
          </button>
          <button onClick={() => { onConfirm(); onCancel(); }}
            className="h-9 px-4 rounded-full text-[12px] font-bold text-white border-none cursor-pointer transition-all shadow-md"
            style={{ background: "linear-gradient(135deg,#E24B4A,#C53736)" }}>
            {confirmText}
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ─── PREVIEW MODAL (CINEMA SCREEN) ────────────────────────────────────────────
function PreviewModal({ video, onClose }) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200]" style={{ background: "rgba(10,10,12,0.85)", backdropFilter: "blur(12px)" }}
        onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="fixed inset-4 sm:inset-0 sm:m-auto z-[201] rounded-[32px] overflow-hidden w-auto sm:w-[min(56rem,calc(100%-2rem))] max-w-4xl max-h-[90vh] h-[min(90vh,640px)] flex flex-col bg-[#131316] border border-white/10 shadow-2xl"
        onClick={e => e.stopPropagation()}>
        
        {/* Cinema Screen Video Player — min-h-0 so tall videos cannot expand past the modal */}
        <div className="relative min-h-0 flex-1 overflow-hidden bg-black flex items-center justify-center group">
          <video
            src={video.videoUrl}
            controls
            autoPlay
            playsInline
            className="max-h-full max-w-full w-auto h-auto object-contain"
          />
          <button onClick={onClose} 
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 text-white flex items-center justify-center cursor-pointer transition-all hover:scale-105 z-10 font-bold">
            ✕
          </button>
        </div>

        {/* Video Metadata Panel */}
        <div className="p-6 bg-[#131316] text-white flex flex-col gap-2.5 flex-shrink-0 max-h-[38%] overflow-y-auto">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
              style={{ background: "rgba(242,154,12,0.15)", color: "#F29A0C", border: "1px solid rgba(242,154,12,0.3)" }}>
              {video.tier} Tier
            </span>
            <span className="text-[11px] text-zinc-400 font-medium">{video.category}</span>
            <span className="text-zinc-600 text-xs">•</span>
            <span className="text-[11px] text-zinc-400 font-semibold">{video.duration}</span>
            <span className="text-zinc-600 text-xs">•</span>
            <span className="text-[11px] text-zinc-400 font-semibold">{video.views.toLocaleString()} Views</span>
          </div>

          <h2 className="text-xl font-bold tracking-tight text-white">{video.title}</h2>
          <p className="text-xs text-zinc-400 leading-relaxed max-w-2xl">
            {video.description || "No description provided."}
          </p>
          <div className="flex items-center gap-2 mt-1.5 border-t border-zinc-800/60 pt-3">
            <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-[#F29A0C]">
              {video.instructor ? video.instructor.charAt(0) : "?"}
            </div>
            <span className="text-xs text-zinc-300 font-semibold">Instructor: {video.instructor || "Not assigned"}</span>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function ContentManagerPage() {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [videos, setVideos]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch]           = useState("");
  const [tierFilter, setTierFilter]   = useState("all");
  const [catFilter, setCatFilter]     = useState("all");
  const [showUpload, setShowUpload]   = useState(false);
  const [editVideo, setEditVideo]     = useState(null);
  const [toast, setToast]             = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [showImport, setShowImport]     = useState(false);
  const [showReorder, setShowReorder]   = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3200); };

  const categoriesList = useMemo(() => {
    const unique = new Set(videos.map(v => v.category).filter(Boolean));
    const list = Array.from(unique);
    return list.length > 0 ? list : ["Beginner Yoga", "Pranayama", "Meditation", "YTT Module", "Diet & Nutrition"];
  }, [videos]);

  const instructorsList = useMemo(() => {
    const unique = new Set(videos.map(v => v.instructor).filter(Boolean));
    const list = Array.from(unique);
    return list; // real instructors only (typed in the field when none exist yet)
  }, [videos]);

  const handleBulkImport = async (importArray) => {
    try {
      const res = await practiceVideoAPI.bulkImportVideos(importArray);
      if (res.data?.success) {
        showToast(`✓ Batch imported ${importArray.length} videos successfully!`);
        fetchVideos();
      }
    } catch (err) {
      showToast("Error importing batch list.");
    }
  };

  const handleBulkPublish = () => {
    const drafts = videos.filter(v => v.status === "Draft");
    if (drafts.length === 0) {
      showToast("No draft videos to publish.");
      return;
    }
    setConfirmDialog({
      title: "Bulk Publish Drafts",
      message: `Are you sure you want to publish all ${drafts.length} draft videos at once?`,
      confirmText: "Publish All",
      onConfirm: async () => {
        try {
          let count = 0;
          await Promise.all(
            drafts.map(async (v) => {
              const parsedDesc = {
                desc: v.description,
                instructor: v.instructor,
                allowComments: v.allowComments !== false,
                autoNotify: v.autoNotify !== false,
                enableSubtitles: !!v.enableSubtitles,
                featured: !!v.featured,
                durationStr: v.duration || "30:00",
                status: "Published"
              };

              const payload = {
                id: v.id,
                title: v.title,
                description: JSON.stringify(parsedDesc),
                category: v.category,
                tier: v.tier,
                videoUrl: v.videoUrl || "/assets/sample.mp4",
                thumbnailUrl: v.thumb,
                durationInMinutes: parseInt((v.duration || "30").split(":")[0]) || 30
              };

              const res = await practiceVideoAPI.updateVideo(v.id, payload);
              if (res.data?.success || res.status === 200) {
                count++;
              }
            })
          );

          await fetchVideos();
          showToast(`✓ Published ${count} draft video${count !== 1 ? "s" : ""}!`);
        } catch (err) {
          console.error("Bulk publish error:", err);
          showToast("Error during bulk publishing.");
          fetchVideos();
        }
      }
    });
  };

  const handleDownloadAnalytics = () => {
    if (videos.length === 0) {
      showToast("No content to analyze.");
      return;
    }
    const headers = ["ID", "Title", "Category", "Tier", "Duration", "Views", "Status", "Featured", "Date"];
    const rows = videos.map(v => [
      v.id,
      `"${v.title.replace(/"/g, '""')}"`,
      v.category,
      v.tier,
      v.duration,
      v.views,
      v.status,
      v.featured,
      v.date
    ]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "divyayoga_content_analytics.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("✓ Content analytics CSV downloaded!");
  };

  const handleSaveReorder = (orderedList) => {
    // Merge order updates back into main list
    setVideos(prev => prev.map(v => {
      const updated = orderedList.find(x => x.id === v.id);
      return updated ? updated : v;
    }));
  };

  const fetchVideos = async () => {
    try {
      const res = await practiceVideoAPI.getVideos();
      if (res.data?.success) {
        const formatted = res.data.data.map(vid => {
          let descText = vid.description || vid.Description || "";
          let instructorName = "";
          let allowComments = true;
          let autoNotify = true;
          let enableSubtitles = false;
          let featuredVal = false;
          let durationString = `${vid.durationInMinutes || vid.DurationInMinutes || 30}:00`;
          let realViews = 0;
          let statusVal = (vid.isActive ?? vid.IsActive ?? true) ? "Published" : "Draft";

          try {
            const parsed = JSON.parse(vid.description || vid.Description);
            if (parsed && typeof parsed === "object") {
              descText = parsed.desc !== undefined ? parsed.desc : (vid.description || vid.Description || "");
              instructorName = parsed.instructor || "";
              allowComments = parsed.allowComments !== false;
              autoNotify = parsed.autoNotify !== false;
              enableSubtitles = !!parsed.enableSubtitles;
              featuredVal = !!parsed.featured;
              durationString = parsed.durationStr || `${vid.durationInMinutes || vid.DurationInMinutes || 30}:00`;
              if (parsed.views !== undefined) realViews = parsed.views;
              if (parsed.status !== undefined) statusVal = parsed.status;
            }
          } catch (e) {
            // fallback
          }



          return {
            id: vid.id || vid.Id,
            title: vid.title || vid.Title,
            description: descText,
            category: vid.category || vid.Category,
            tier: vid.tier || vid.Tier,
            videoUrl: vid.videoUrl || vid.VideoUrl,
            thumb: vid.thumbnailUrl || vid.ThumbnailUrl || "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80",
            duration: durationString,
            instructor: instructorName,
            views: realViews,
            status: statusVal,
            featured: featuredVal,
            allowComments,
            autoNotify,
            enableSubtitles,
            date: new Date(vid.createdAt || vid.CreatedAt || new Date()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
            createdTs: (vid.createdAt || vid.CreatedAt) ? new Date(vid.createdAt || vid.CreatedAt).getTime() : 0
          };
        });
        setVideos(formatted);
      }
    } catch (err) {
      console.error("Failed to load videos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleSavePracticeVideo = async (payload) => {
    try {
      const res = await practiceVideoAPI.createVideo(payload);
      if (res.data?.success) {
        showToast("✓ Video saved successfully!");
        fetchVideos();
        setShowUpload(false);
      }
    } catch (err) {
      showToast("Error saving video details.");
    }
  };

  const gridContainerRef = useRef(null);
  const [columnsCount, setColumnsCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (gridContainerRef.current) {
        const width = gridContainerRef.current.clientWidth;
        const cardMinWidth = 240;
        const gap = 16;
        const cols = Math.max(1, Math.floor((width + gap) / (cardMinWidth + gap)));
        setColumnsCount(cols);
      }
    };

    handleResize();

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    if (gridContainerRef.current) {
      resizeObserver.observe(gridContainerRef.current);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const itemsPerPage = useMemo(() => {
    return Math.max(3, columnsCount * 3); // Auto pagination: EXACTLY 3 ROWS per screen
  }, [columnsCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, tierFilter, catFilter]);

  const filtered = useMemo(() => videos.filter(v => {
    const q = search.toLowerCase().trim();
    const matchesSearch = !q ||
      String(v.title || "").toLowerCase().includes(q) ||
      String(v.category || "").toLowerCase().includes(q) ||
      String(v.instructor || "").toLowerCase().includes(q) ||
      String(v.description || "").toLowerCase().includes(q) ||
      String(v.tier || "").toLowerCase().includes(q) ||
      String(v.status || "").toLowerCase().includes(q);

    const matchesStatus = statusFilter === "all" || String(v.status || "").toLowerCase() === statusFilter.toLowerCase();
    const matchesTier = matchTier(v.tier, tierFilter);
    const matchesCategory = catFilter === "all" || String(v.category || "").toLowerCase() === catFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesTier && matchesCategory;
  }), [videos, search, statusFilter, tierFilter, catFilter]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const PAGE = filtered.slice(startIndex, startIndex + itemsPerPage);

  const publishVideo = async (videoObjOrId) => {
    let videoItem = typeof videoObjOrId === "object" ? videoObjOrId : videos.find(v => v.id === videoObjOrId);
    if (!videoItem) return;
    try {
      const parsedDesc = {
        desc: videoItem.description,
        instructor: videoItem.instructor,
        allowComments: videoItem.allowComments,
        autoNotify: videoItem.autoNotify,
        enableSubtitles: videoItem.enableSubtitles,
        featured: videoItem.featured,
        durationStr: videoItem.duration,
        status: "Published"
      };
      
      const payload = {
        id: videoItem.id,
        title: videoItem.title,
        description: JSON.stringify(parsedDesc),
        category: videoItem.category,
        tier: videoItem.tier,
        videoUrl: videoItem.videoUrl,
        thumbnailUrl: videoItem.thumb,
        durationInMinutes: parseInt(videoItem.duration.split(":")[0]) || 30
      };
      
      const res = await practiceVideoAPI.updateVideo(videoItem.id, payload);
      if (res.data?.success) {
        showToast("✓ Video published successfully!");
        fetchVideos();
      }
    } catch (err) {
      showToast("Failed to publish video.");
    }
  };
  const archiveVideo = async (id, title) => {
    setConfirmDialog({
      title: "Delete Practice Video",
      message: `Are you sure you want to delete "${title}"? This action is permanent and cannot be undone.`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await practiceVideoAPI.deleteVideo(id);
          setVideos(vs => vs.filter(v => v.id !== id));
          showToast("✓ Video deleted successfully");
        } catch (err) {
          showToast("Failed to delete video");
        }
      }
    });
  };
  const saveEdit = async (payload) => {
    try {
      const res = await practiceVideoAPI.updateVideo(payload.id, {
        title: payload.title,
        description: payload.description,
        category: payload.category,
        tier: payload.tier,
        videoUrl: payload.videoUrl,
        thumbnailUrl: payload.thumbnailUrl,
        durationInMinutes: payload.durationInMinutes
      });
      if (res.data?.success) {
        showToast("✓ Changes saved successfully!");
        fetchVideos();
      }
    } catch (err) {
      showToast("Error updating video details.");
    }
  };

  const TABS = ["all","Published","Draft","Archived"];

  const stats = useMemo(() => {
    const total = videos.length;
    const published = videos.filter(v => v.status === "Published").length;
    const drafts = videos.filter(v => v.status === "Draft").length;
    const archived = videos.filter(v => v.status === "Archived").length;
    
    let totalSecs = 0;
    videos.forEach(v => {
      if (!v.duration) return;
      const parts = v.duration.split(":").map(Number);
      if (parts.length === 3) {
        totalSecs += (parts[0] * 3600) + (parts[1] * 60) + parts[2];
      } else if (parts.length === 2) {
        totalSecs += (parts[0] * 60) + parts[1];
      } else if (parts.length === 1 && !isNaN(parts[0])) {
        totalSecs += parts[0] * 60;
      }
    });

    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    const formattedDuration = `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;

    const publishedPct = total > 0 ? Math.round((published / total) * 100) : 0;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const addedThisWeek = videos.filter(v => v.createdTs && v.createdTs >= weekAgo).length;

    return {
      total,
      addedThisWeek,
      published,
      drafts,
      archived,
      totalHours: formattedDuration,
      publishedPct: `${publishedPct}% of total`
    };
  }, [videos]);

  const tierBreakdown = useMemo(() => {
    const counts = {};
    dynamicTiers.forEach(t => { counts[t] = 0; });
    videos.forEach(v => {
      dynamicTiers.forEach(t => {
        if (matchTier(v.tier, t)) {
          counts[t] = (counts[t] || 0) + 1;
        }
      });
    });
    const total = videos.length || 1;
    const gradients = [
      "linear-gradient(90deg,#AAA,#CCC)",
      "linear-gradient(90deg,#185FA5,#378ADD)",
      "linear-gradient(90deg,#534AB7,#7F77DD)",
      `linear-gradient(90deg,${T.orange},${T.orange2})`,
      `linear-gradient(90deg,${T.olive},${T.olive2})`,
      "linear-gradient(90deg,#E24B4A,#F87171)",
      "linear-gradient(90deg,#8B5CF6,#A78BFA)"
    ];
    return dynamicTiers.map((t, i) => {
      const n = counts[t] || 0;
      return {
        tier: t,
        n,
        pct: Math.round((n / total) * 100),
        grad: gradients[i % gradients.length]
      };
    });
  }, [videos, dynamicTiers]);

  const recentActivities = useMemo(() => {
    return videos.slice(0, 5).map((v, idx) => {
      const iconMap = [FiVideo, FiCheckCircle, FiEdit, FiStar, FiActivity];
      const colorMap = [T.olive, T.greenDk, T.amber, T.orange, T.olive];
      const bgMap = ["rgba(107,138,90,0.1)", "rgba(34,197,94,0.1)", "rgba(245,158,11,0.1)", "rgba(242,154,12,0.1)", "rgba(107,138,90,0.1)"];
      return {
        icon: iconMap[idx % 5],
        color: colorMap[idx % 5],
        bg: bgMap[idx % 5],
        text: `"${v.title}" uploaded as ${v.tier}`,
        time: v.date
      };
    });
  }, [videos]);

  if (loading && videos.length === 0) {
    return (
      <AdminDashboardLayout activeNav="content">
        <BrandLoader 
          message="Loading Content Manager..." 
          subMessage="Fetching practice videos, categories & library assets" 
        />
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout activeNav="content">
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-20,0],x:[0,14,0] }} transition={{ duration:14,repeat:Infinity,ease:"easeInOut" }}
          className="fixed w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)",filter:"blur(80px)",top:-160,right:-80,zIndex:0 }}/>
        <motion.div animate={{ y:[0,16,0],x:[0,-10,0] }} transition={{ duration:17,repeat:Infinity,ease:"easeInOut" }}
          className="fixed w-[360px] h-[360px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.06),transparent 70%)",filter:"blur(80px)",bottom:-80,left:40,zIndex:0 }}/>

        <div className="relative z-10 w-full min-w-0">

          {/* Topbar */}
          <motion.div initial={{ opacity:0,y:-16 }} animate={{ opacity:1,y:0 }}
            className="ad-toolbar flex items-center justify-between mb-6">
            <div>
              <h1 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif",fontSize:26,letterSpacing:"-1px" }}>Content Manager</h1>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>{stats.total} videos · {stats.published} published · {stats.drafts} drafts</p>
            </div>
            <div className="flex gap-2">
              <motion.button whileHover={{ y:-1 }} onClick={() => setShowImport(true)}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
                style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,cursor:"pointer",fontFamily:"'Inter',sans-serif" }}>
                <FiUpload size={13}/> Bulk Import
              </motion.button>
              <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }} onClick={() => setShowUpload(true)}
                className="h-9 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,boxShadow:`0 5px 16px rgba(107,138,90,0.38)`,border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif" }}>
                <FiPlus size={13}/> Upload Video
              </motion.button>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="ad-kpi grid grid-cols-5 gap-3 mb-5">
            <StatCard icon={FiVideo}      value={stats.total}   label="Total Videos"   trend={`+${stats.addedThisWeek} this week`}       iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`} cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.06}/>
            <StatCard icon={FiCheckCircle}value={stats.published}   label="Published"       trend={stats.publishedPct}        iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`} cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.10}/>
            <StatCard icon={FiClock}      value={stats.drafts}   label="Drafts"          trend="Needs review" trendUp={false} iconBg="rgba(245,158,11,0.2)" accentColor={T.amber} delay={0.14}/>
            <StatCard icon={FiClock}      value={stats.totalHours} label="Total Duration"  trend="Across all tiers"    iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`} delay={0.18}/>
            <StatCard icon={FiEyeOff}     value={stats.archived}   label="Archived"        trend="Hidden from users" trendUp={false} iconBg="rgba(226,75,74,0.15)" accentColor={T.red} cardBg="linear-gradient(145deg,rgba(226,75,74,0.08),rgba(252,235,235,0.4))" delay={0.22}/>
          </div>

          {/* Main grid */}
          <div className="ad-1col grid grid-cols-[1fr_320px] gap-4">

            {/* LEFT: Video library */}
            <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.1 }}
              className="rounded-[24px] relative" style={glass}>

              {/* Toolbar */}
              <div className="ad-toolbar ad-pad flex items-center justify-between px-5 py-3.5 border-b rounded-t-[24px]" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
                <div className="ad-scroll flex gap-1.5">
                  {TABS.map(tab => (
                    <motion.button key={tab} whileTap={{ scale:0.94 }} onClick={() => setStatusFilter(tab)}
                      className="px-3.5 py-2 rounded-full text-[12px] font-semibold border cursor-pointer"
                      style={statusFilter===tab
                        ? { background:`linear-gradient(135deg,${T.olive},${T.olive2})`,color:"#fff",borderColor:"transparent",boxShadow:`0 4px 14px rgba(107,138,90,0.35)`,fontFamily:"'Inter',sans-serif" }
                        : { background:"rgba(255,255,255,0.85)",color:T.mid,borderColor:T.border,fontFamily:"'Inter',sans-serif" }}>
                      {tab === "all" ? "All Videos" : tab}
                    </motion.button>
                  ))}
                </div>
                <div className="ad-filters flex gap-2 items-center">
                  <div className="ad-filters-full flex items-center gap-2 rounded-[13px] px-3 py-2 focus-within:border-[#6B8A5A] transition-all" style={{ background:"rgba(255,255,255,0.9)",border:`1px solid ${T.border}` }}>
                    <FiSearch size={13} style={{ color:T.light }} className="shrink-0" />
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search videos, instructor, tier…"
                      className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F] w-[180px]"
                      style={{ fontFamily:"'Inter',sans-serif" }}
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="text-zinc-400 hover:text-zinc-600 border-none bg-transparent cursor-pointer p-0 flex items-center justify-center"
                        title="Clear search"
                      >
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                  <CustomDropdown
                    label="All Tiers"
                    value={tierFilter}
                    onChange={setTierFilter}
                    options={[
                      { value: "all", label: "All Tiers" },
                      ...dynamicTiers.map(o => ({ value: o, label: o }))
                    ]}
                  />
                  <CustomDropdown
                    label="All Categories"
                    value={catFilter}
                    onChange={setCatFilter}
                    align="right"
                    options={[
                      { value: "all", label: "All Categories" },
                      ...categoriesList.map(o => ({ value: o, label: o }))
                    ]}
                  />
                </div>
              </div>

              {/* Video Grid */}
              <div ref={gridContainerRef} className="ad-pad p-5">
                {filtered.length > 0 ? (
                  <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }} className="animate-fade-in">
                    {PAGE.map((v, i) => (
                      <motion.div key={v.id}
                        initial={{ opacity:0,x:-6 }} animate={{ opacity:1,x:0 }}
                        transition={{ delay:i*0.04 }}
                        whileHover={{ y:-5, boxShadow:"0 20px 50px rgba(0,0,0,0.1)", borderColor:"rgba(107,138,90,0.25)" }}
                        onClick={() => setEditVideo(v)}
                        className="rounded-[18px] overflow-hidden border cursor-pointer transition-all"
                        style={{ background:"rgba(255,255,255,0.85)",borderColor:"rgba(255,255,255,0.95)",boxShadow:"0 8px 28px rgba(0,0,0,0.05)" }}>
                        {/* Thumbnail */}
                        <div className="relative overflow-hidden" style={{ height:160,background:"#1A1A1A" }}>
                          <img src={v.thumb} alt="" loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-[1200ms]"
                            style={{ transform:"scale(1)" }}
                            onMouseEnter={e => e.currentTarget.style.transform="scale(1.07)"}
                            onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}/>
                          <div className="absolute inset-0" style={{ background:"linear-gradient(to top,rgba(0,0,0,0.55) 0%,transparent 55%)" }}/>
                          {/* Play overlay */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-250"
                            onClick={(e) => { e.stopPropagation(); setPreviewVideo(v); }}>
                            <div className="w-11 h-11 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                              style={{ background:"rgba(255,255,255,0.18)",border:"2px solid rgba(255,255,255,0.5)",backdropFilter:"blur(8px)" }}>
                              <span className="text-white text-sm ml-0.5">▶</span>
                            </div>
                          </div>
                          <div className="absolute bottom-2 right-2.5 rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                            style={{ background:"rgba(0,0,0,0.7)" }}>{v.duration}</div>
                          {v.featured && (
                            <div className="absolute top-2 left-2.5 rounded-full px-2 py-0.5 text-[9px] font-black text-white"
                              style={{ background:"rgba(242,154,12,0.9)" }}>★ FEATURED</div>
                          )}
                        </div>

                        {/* Body */}
                        <div className="p-3.5">
                          <div className="flex gap-1.5 mb-2 flex-wrap">
                            <TierPill tier={v.tier}/>
                            <StatusPill status={v.status}/>
                          </div>
                          <p className="text-[13px] font-bold text-[#1F1F1F] mb-1.5 leading-[1.35]">{v.title}</p>
                          <p className="text-[11px] mb-2" style={{ color:T.muted }}>{v.category} · {v.instructor || "Not assigned"}</p>
                          <p className="text-[11px] font-semibold mb-2" style={{ color:T.muted }}>{v.views.toLocaleString()} views</p>
                          <div className="h-[3px] rounded-full overflow-hidden mb-1" style={{ background:"rgba(239,229,216,0.5)" }}>
                            <div className="h-full rounded-full" style={{ width:`${Math.min(v.views/100,100)}%`, background:`linear-gradient(90deg,${T.olive},${T.olive2})` }}/>
                          </div>
                          <p className="text-[10px]" style={{ color:T.light }}>Added {v.date}</p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1.5 px-3.5 py-2.5 border-t" style={{ borderColor:"rgba(239,229,216,0.5)" }}
                          onClick={e => e.stopPropagation()}>
                          {v.status === "Draft" && (
                            <motion.button whileHover={{ y:-1 }} onClick={() => publishVideo(v.id)}
                              className="h-7 px-3 rounded-full text-white text-[11px] font-bold flex items-center gap-1 border-none cursor-pointer flex-shrink-0"
                              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, fontFamily:"'Inter',sans-serif" }}>
                              <FiSend size={10}/> Publish
                            </motion.button>
                          )}
                          {v.status === "Published" && (
                            <motion.button whileHover={{ y:-1 }} onClick={() => setPreviewVideo(v)}
                              className="h-7 px-3 rounded-full text-[11px] font-semibold flex items-center gap-1 cursor-pointer"
                              style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,fontFamily:"'Inter',sans-serif" }}>
                              <FiEye size={10}/> Preview
                            </motion.button>
                          )}
                          <motion.button whileHover={{ y:-1 }} onClick={() => setEditVideo(v)}
                            className="h-7 px-3 rounded-full text-[11px] font-semibold flex items-center gap-1 cursor-pointer"
                            style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,fontFamily:"'Inter',sans-serif" }}>
                            <FiEdit size={10}/> Edit
                          </motion.button>
                          <motion.button whileHover={{ background:"rgba(226,75,74,0.14)" }} onClick={() => archiveVideo(v.id,v.title)}
                            className="h-7 w-7 rounded-full flex items-center justify-center cursor-pointer border"
                            style={{ background:"rgba(226,75,74,0.08)",borderColor:"rgba(226,75,74,0.2)",color:T.red }}>
                            <FiArchive size={11}/>
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
                    <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4 border border-zinc-200"
                      style={{ background:"rgba(107,138,90,0.06)", borderColor:"rgba(107,138,90,0.2)" }}>
                      <FiVideo size={24} style={{ color: T.olive }} />
                    </div>
                    <h3 className="font-bold text-zinc-800 text-sm mb-1">No Videos Found</h3>
                    <p className="text-xs text-zinc-500 max-w-xs leading-relaxed">
                      There are no practice videos uploaded matching the selected filters.
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination — Smart Scalable Suite (Exact User Management Style) */}
              <div
                className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t rounded-b-[24px]"
                style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.4)" }}
              >
                <div className="text-[12px] font-bold text-zinc-500">
                  Showing <span className="text-zinc-900">{filtered.length > 0 ? startIndex + 1 : 0}</span> to{" "}
                  <span className="text-zinc-900">{Math.min(startIndex + itemsPerPage, filtered.length)}</span> of{" "}
                  <span className="text-zinc-900">{filtered.length}</span> videos
                </div>

                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Active Page Counter Pill */}
                    <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                      Page {currentPage} of {totalPages}
                    </span>

                    {/* Jump to First Page Button */}
                    <motion.button
                      whileTap={{ scale: 0.92 }}
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(1)}
                      title="First Page"
                      className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                    >
                      <FiChevronsLeft size={14} />
                    </motion.button>

                    {/* Previous Page Button */}
                    <motion.button
                      whileTap={{ scale: 0.92 }}
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      title="Previous Page"
                      className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                    >
                      <FiChevronLeft size={14} /> Prev
                    </motion.button>

                    {/* Smart Windowed Page Number Buttons */}
                    <div className="flex items-center gap-1">
                      {(() => {
                        let pages = [];
                        if (totalPages <= 7) {
                          pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                        } else if (currentPage <= 4) {
                          pages = [1, 2, 3, 4, 5, "...", totalPages];
                        } else if (currentPage >= totalPages - 3) {
                          pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                        } else {
                          pages = [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
                        }

                        return pages.map((item, idx) => {
                          if (item === "...") {
                            return (
                              <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                                •••
                              </span>
                            );
                          }

                          const isActive = currentPage === item;
                          return (
                            <motion.button
                              key={item}
                              whileTap={{ scale: 0.92 }}
                              onClick={() => setCurrentPage(item)}
                              className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                                isActive
                                  ? "text-white shadow-xs"
                                  : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                              }`}
                              style={
                                isActive
                                  ? {
                                      background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                                      borderColor: "transparent",
                                      boxShadow: `0 4px 12px rgba(107,138,90,0.35)`,
                                    }
                                  : {}
                              }
                            >
                              {item}
                            </motion.button>
                          );
                        });
                      })()}
                    </div>

                    {/* Next Page Button */}
                    <motion.button
                      whileTap={{ scale: 0.92 }}
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      title="Next Page"
                      className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                    >
                      Next <FiChevronRight size={14} />
                    </motion.button>

                    {/* Jump to Last Page Button */}
                    <motion.button
                      whileTap={{ scale: 0.92 }}
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      title="Last Page"
                      className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                    >
                      <FiChevronsRight size={14} />
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* RIGHT panel */}
            <div className="flex flex-col gap-4">

              {/* Upload zone */}
              <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.12 }}
                className="rounded-[22px] p-5" style={glass}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-black text-[15px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif",letterSpacing:"-0.3px" }}>Quick Upload</p>
                    <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>MP4, MOV, AVI up to 4GB</p>
                  </div>
                  <motion.button whileHover={{ y:-1 }} onClick={() => setShowUpload(true)}
                    className="h-8 px-3.5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5 border-none cursor-pointer"
                    style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, fontFamily:"'Inter',sans-serif" }}>
                    <FiPlus size={12}/> Full Form
                  </motion.button>
                </div>
                <motion.div whileHover={{ y:-2, borderColor:T.olive, background:"rgba(107,138,90,0.08)" }}
                  onClick={() => setShowUpload(true)}
                  className="rounded-[18px] border-2 border-dashed p-6 text-center cursor-pointer transition-all"
                  style={{ borderColor:"rgba(107,138,90,0.3)", background:"rgba(107,138,90,0.04)" }}>
                  <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3"
                    style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 8px 24px rgba(107,138,90,0.35)` }}>
                    <FiUpload size={22} style={{ color:"#fff" }}/>
                  </div>
                  <p className="text-[13px] font-bold text-[#1F1F1F] mb-1">Drop video here</p>
                  <p className="text-[12px]" style={{ color:T.muted }}>or click to browse files</p>
                </motion.div>
              </motion.div>

              {/* Tier breakdown */}
              <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.16 }}
                className="rounded-[22px] p-5" style={glass}>
                <p className="font-black text-[15px] text-[#1F1F1F] mb-4" style={{ fontFamily:"'Poppins',sans-serif",letterSpacing:"-0.3px" }}>Content by Tier</p>
                {tierBreakdown.map(d => (
                  <div key={d.tier} className="mb-3 last:mb-0">
                    <div className="flex justify-between mb-1">
                      <span className="text-[12px] font-semibold" style={{ color:T.mid }}>{d.tier}</span>
                      <div className="flex gap-2"><span className="text-[12px] font-black text-[#1F1F1F]">{d.n}</span><span className="text-[11px]" style={{ color:T.light }}>{d.pct}%</span></div>
                    </div>
                    <div className="h-[5px] rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
                      <motion.div initial={{ width:0 }} animate={{ width:`${d.pct}%` }} transition={{ duration:0.9, ease:"easeOut" }}
                        className="h-full rounded-full" style={{ background:d.grad }}/>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Recent activity */}
              <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.20 }}
                className="rounded-[22px] p-5" style={glass}>
                <p className="font-black text-[15px] text-[#1F1F1F] mb-4" style={{ fontFamily:"'Poppins',sans-serif",letterSpacing:"-0.3px" }}>Recent Activity</p>
                {recentActivities.map((a, i) => (
                  <div key={i} className="flex items-center gap-2.5 py-2.5 border-b last:border-b-0" style={{ borderColor:"rgba(239,229,216,0.4)" }}>
                    <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background:a.bg }}>
                      <a.icon size={13} style={{ color:a.color }}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-[#1F1F1F] truncate">{a.text}</p>
                      <p className="text-[10px] mt-0.5" style={{ color:T.light }}>{a.time}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Quick actions */}
              <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.24 }}
                className="rounded-[22px] p-5" style={glass}>
                <p className="font-black text-[15px] text-[#1F1F1F] mb-3" style={{ fontFamily:"'Poppins',sans-serif",letterSpacing:"-0.3px" }}>Quick Actions</p>
                <div className="flex flex-col gap-2">
                  {[
                    { icon:FiRefreshCw, label:"Reorder Series",     accent:T.orange, bg:"rgba(242,154,12,0.07)",  border:"rgba(242,154,12,0.18)",  c:"#C8660A", action:() => setShowReorder(true) },
                    { icon:FiSend,      label:"Bulk Publish Drafts",accent:T.olive,  bg:"rgba(107,138,90,0.07)",  border:"rgba(107,138,90,0.18)",  c:"#27500A", action:handleBulkPublish },
                    { icon:FiActivity,  label:"Content Analytics",  accent:T.orange, bg:"rgba(242,154,12,0.07)",  border:"rgba(242,154,12,0.18)",  c:"#C8660A", action:handleDownloadAnalytics },
                  ].map(a => (
                    <motion.button key={a.label} whileHover={{ y:-1 }} onClick={a.action}
                      className="w-full py-2.5 px-3.5 rounded-[13px] flex items-center gap-2.5 text-[13px] font-semibold cursor-pointer border text-left"
                      style={{ background:a.bg, borderColor:a.border, color:a.c, fontFamily:"'Inter',sans-serif", transition:"background 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background = a.bg.replace("0.07","0.14")}
                      onMouseLeave={e => e.currentTarget.style.background = a.bg}>
                      <div className="w-7 h-7 rounded-[9px] flex items-center justify-center flex-shrink-0"
                        style={{ background:a.bg.replace("0.07","0.15") }}>
                        <a.icon size={13} style={{ color:a.accent }}/>
                      </div>
                      {a.label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Drawers */}
        <AnimatePresence>
          {showUpload && <UploadDrawer onClose={() => setShowUpload(false)} showToast={showToast} onSave={handleSavePracticeVideo} categories={categoriesList} instructors={instructorsList}/>}
        </AnimatePresence>
        <AnimatePresence>
          {editVideo && (
            <EditDrawer video={editVideo} onClose={() => setEditVideo(null)}
              showToast={showToast} onSave={saveEdit} onPublish={publishVideo} onArchive={archiveVideo} categories={categoriesList} instructors={instructorsList}/>
          )}
        </AnimatePresence>

        {/* Dynamic Modals */}
        <AnimatePresence>
          {showImport && <BulkImportModal onClose={() => setShowImport(false)} showToast={showToast} onImport={handleBulkImport}/>}
          {showReorder && <ReorderModal videos={videos} onClose={() => setShowReorder(false)} showToast={showToast} onSaveReorder={handleSaveReorder}/>}
          {previewVideo && <PreviewModal video={previewVideo} onClose={() => setPreviewVideo(null)} />}
          {confirmDialog && (
            <ConfirmationDialog 
              title={confirmDialog.title} 
              message={confirmDialog.message} 
              confirmText={confirmDialog.confirmText} 
              onConfirm={confirmDialog.onConfirm} 
              onCancel={() => setConfirmDialog(null)} 
            />
          )}
        </AnimatePresence>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div key={toast} initial={{ opacity:0,y:16,scale:0.95 }} animate={{ opacity:1,y:0,scale:1 }} exit={{ opacity:0,y:16 }}
              className="ad-toast fixed bottom-7 right-7 z-[200] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
              style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",boxShadow:"0 16px 48px rgba(0,0,0,0.25)",fontFamily:"'Inter',sans-serif" }}>
              <FiCheck size={16} style={{ color:T.olive }}/>{toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
}